/**
 * Admin Project Management Panel JavaScript
 * Handles project CRUD operations and Firebase synchronization
 */

class AdminProjectPanel {
  constructor() {
    this.apiBase = window.adminPanelConfig?.apiBase || 'http://localhost:3000/api';
    this.firebaseProjectId = window.adminPanelConfig?.firebaseProjectId || '';
    this.projects = [];
    this.filteredProjects = [];
    this.currentProject = null;
    // Firebase mode: Check config or default to false (use dummy data for development)
    this.useFirebase = window.adminPanelConfig?.useFirebase !== undefined 
      ? window.adminPanelConfig.useFirebase 
      : false; // Default to dummy data for development
    this.firebaseManager = window.firebaseManager;
    this.sortField = 'projectNumber';
    this.sortDirection = 'asc';
    this.dummyDataStorageKey = 'atlio_dummy_projects';
    this.expensesStorageKey = 'atlio_company_expenses';
    this.isEditMode = false;
    this.expenses = [];
    this.currentExpense = null;
    
    // Single source of truth for all status options
    this.STATUS_OPTIONS = [
      'Not Started',
      'Invoice Sent',
      'In Progress (1st Draft)',
      '1st Draft Done',
      'Awaiting Feedback',
      'In Progress (2nd Draft)',
      '2nd Draft Done',
      'Completed',
      'Not Won',
      'On Hold'
    ];
    
    this.init();
  }

  // Timeline cell renderer: shows progress bar with Next Milestone label
  renderTimelineCell(project) {
    // Use project completion value for progress (independent of dates)
    const progressPercent = Math.max(0, Math.min(100, parseInt(project.completion) || 0));
    
    // Format next milestone date
    const nextMilestoneDate = project.nextMilestone ? this.formatDate(project.nextMilestone) : '';
    const isCompleted = (project.status || '').toLowerCase().includes('completed');
    
    // Determine label text: show "Milestone Dates" if nextMilestone is empty OR status is Completed
    const milestoneLabel = (!nextMilestoneDate || isCompleted) 
      ? 'Milestone Dates' 
      : `Next Milestone: ${nextMilestoneDate}`;
    
    // Store project ID for popup and drag functionality
    const projectId = project.id || '';
    
    // Timeline is not editable - removed drag handle for edit mode
    const editModeClass = '';
    const dragHandle = '';
    
      return `
      <div class="timeline-cell flex flex-col gap-2 ${editModeClass}" data-project-id="${projectId}">
        <!-- Next Milestone Label (clickable) -->
        <button type="button" 
                class="next-milestone-label text-sm text-blue-400 hover:text-blue-300 transition-colors duration-200 text-center cursor-pointer"
                onclick="adminPanel.showMilestoneDatesPopup('${projectId}')"
                title="Click to view all milestone dates">
          ${this.escapeHtml(milestoneLabel)}
          </button>
    
        <!-- Progress Bar -->
      <div class="flex items-center gap-2">
          <div class="relative flex-1" style="max-width: 300px; height: 8px;">
            <div class="rounded-full bg-slate-700 absolute inset-0" style="height: 8px;">
              <div class="rounded-full transition-all duration-300 bg-blue-500 absolute top-0 left-0" 
                   style="height: 8px; width: ${progressPercent}%;"></div>
          </div>
            ${dragHandle}
        </div>
        <span class="text-xs font-bold text-white whitespace-nowrap">${Math.round(progressPercent)}%</span>
        </div>
      </div>
    `;
  }

  updateTimelineCell(projectId) {
    const project = this.projects.find(p => p.id === projectId);
    if (!project) return;
    
    // Find the row containing this project
    const tbody = document.getElementById('projects-table-body');
    if (!tbody) return;
    
    const rows = Array.from(tbody.querySelectorAll('tr'));
    const row = rows.find(r => {
      const cell = r.querySelector(`[data-id="${projectId}"]`);
      return cell !== null;
    });
    
    if (!row) return;
    
    // Find the timeline cell (it's the cell containing the timeline)
    const timelineCell = Array.from(row.querySelectorAll('td')).find(td => 
      td.innerHTML.includes('timeline-cell') || td.innerHTML.includes('next-milestone-label')
    );
    
    if (timelineCell) {
      timelineCell.innerHTML = this.renderTimelineCell(project);
      // Timeline is not editable - removed drag handlers
    }
  }

  // Show milestone dates popup
  showMilestoneDatesPopup(projectId) {
    const project = this.projects.find(p => p.id === projectId);
    if (!project) return;
    
    // Format dates
    const firstDraft = project.firstDraft ? this.formatDate(project.firstDraft) : '';
    const secondDraft = project.secondDraft ? this.formatDate(project.secondDraft) : '';
    const nextMilestone = project.nextMilestone ? this.formatDate(project.nextMilestone) : '';
    const finalSubmission = project.finalSubmission ? this.formatDate(project.finalSubmission) : '';
    
    // Create popup HTML
    const popupHtml = `
      <div id="milestone-dates-popup" class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center animate-in fade-in duration-300">
        <div class="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 p-6 max-w-md w-full mx-4 animate-in slide-in-from-bottom-4 duration-500">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-xl font-bold text-white">Milestone Dates</h3>
            <button type="button" 
                    onclick="document.getElementById('milestone-dates-popup')?.remove()"
                    class="text-gray-400 hover:text-white transition-colors duration-200 p-2 hover:bg-gray-700 rounded-lg">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="space-y-3">
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <span class="text-sm font-medium text-gray-300">1st Draft:</span>
              <span class="text-sm text-white">${this.escapeHtml(firstDraft || '')}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <span class="text-sm font-medium text-gray-300">2nd Draft:</span>
              <span class="text-sm text-white">${this.escapeHtml(secondDraft || '')}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-gray-700">
              <span class="text-sm font-medium text-gray-300">Next Milestone:</span>
              <span class="text-sm text-white">${this.escapeHtml(nextMilestone || '')}</span>
            </div>
            <div class="flex justify-between items-center py-2">
              <span class="text-sm font-medium text-gray-300">Final:</span>
              <span class="text-sm text-white">${this.escapeHtml(finalSubmission || '')}</span>
            </div>
          </div>
        </div>
      </div>
    `;
    
    // Remove existing popup if any
    const existingPopup = document.getElementById('milestone-dates-popup');
    if (existingPopup) {
      existingPopup.remove();
    }
    
    // Add popup to body
    document.body.insertAdjacentHTML('beforeend', popupHtml);
    
    // Close on backdrop click
    const popup = document.getElementById('milestone-dates-popup');
    popup.addEventListener('click', (e) => {
      if (e.target === popup) {
        popup.remove();
      }
    });
    
    // Close on Escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        popup.remove();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  }

  // Bind drag handlers for timeline progress bar in edit mode
  bindTimelineDragHandlers(timelineCell, projectId) {
    const dragHandle = timelineCell.querySelector('.timeline-drag-handle');
    if (!dragHandle) return;
    
    let isDragging = false;
    let startX = 0;
    let startPercent = 0;
    
    const progressBar = timelineCell.querySelector('.relative');
    if (!progressBar) return;
    
    const getPercentFromX = (clientX) => {
      const rect = progressBar.getBoundingClientRect();
      const x = clientX - rect.left;
      const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
      // Round to nearest 5%
      return Math.round(percent / 5) * 5;
    };
    
    const updateProgress = (percent) => {
      const project = this.projects.find(p => p.id === projectId);
      if (!project) return;
      
      project.completion = percent;
      
      // Update visual
      const fillBar = timelineCell.querySelector('.bg-blue-500');
      const percentLabel = timelineCell.querySelector('.text-xs.font-bold');
      const handle = timelineCell.querySelector('.timeline-drag-handle');
      
      if (fillBar) fillBar.style.width = `${percent}%`;
      if (percentLabel) percentLabel.textContent = `${Math.round(percent)}%`;
      if (handle) {
        handle.style.left = `${percent}%`;
        // Preserve the transform for vertical centering
        handle.style.transform = 'translate(-50%, -50%)';
      }
      
      // Update data attribute
      if (handle) handle.setAttribute('data-completion', percent);
    };
    
    dragHandle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      isDragging = true;
      startX = e.clientX;
      startPercent = parseInt(dragHandle.getAttribute('data-completion')) || 0;
      dragHandle.classList.add('cursor-grabbing');
      dragHandle.classList.remove('cursor-grab');
    });
    
    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      
      const deltaX = e.clientX - startX;
      const rect = progressBar.getBoundingClientRect();
      const deltaPercent = (deltaX / rect.width) * 100;
      const newPercent = Math.max(0, Math.min(100, startPercent + deltaPercent));
      const roundedPercent = Math.round(newPercent / 5) * 5;
      
      updateProgress(roundedPercent);
    });
    
    document.addEventListener('mouseup', async () => {
      if (isDragging) {
        isDragging = false;
        dragHandle.classList.remove('cursor-grabbing');
        dragHandle.classList.add('cursor-grab');
        
        // Save the project completion value
        const project = this.projects.find(p => p.id === projectId);
        if (project) {
          const completion = parseInt(dragHandle.getAttribute('data-completion')) || 0;
          await this.inlineUpdate(projectId, 'completion', completion.toString());
        }
      }
    });
  }

  // ============================================
  // DUMMY DATA METHODS (For Development)
  // ============================================
  
  /**
   * Generate dummy projects for development
   */
  generateDummyProjects() {
    const services = ['Web Dev (Shopify)', 'Web Design', 'SEO', 'Marketing', 'Consulting', 'E-commerce'];
    const clients = ['Acme Corp', 'TechStart Inc', 'Retail Solutions', 'Digital Agency', 'E-commerce Pro', 'Brand Studio'];
    const emails = ['john@acme.com', 'sarah@techstart.com', 'mike@retail.com', 'lisa@digital.com', 'david@ecommerce.com', 'emma@brand.com'];
    
    return [
      {
        id: 'dummy-1',
        projectNumber: 1,
        projectName: 'E-commerce Platform Redesign',
        client: clients[0],
        email: emails[0],
        serviceType: services[0],
        status: this.STATUS_OPTIONS[2], // 'In Progress (1st Draft)'
        completion: 65,
        version: '2.1.0',
        projectType: 'Standard',
        priority: 'High',
        active: true,
        invoiceNumber: 1001,
        invoiceDate: '2024-10-15',
        invoiceTotal: 15000,
        fiftyPercentCost: 7500,
        fiftyPercentPaid: 'Yes',
        tax: 3000,
        devCost: 5000,
        netProfit: 7000,
        clientPaid: 12000,
        remainingToBePaid: 3000,
        paidToDev: 4500,
        extraDevCost: 500,
        shopifyThemeId: '187271971158',
        shopifyStoreDomain: 'acme.myshopify.com',
        shopifyStoreUrl: 'https://acme.myshopify.com/',
        nextSteps: 'Final review and deployment',
        completed: 'Homepage redesign, product pages, checkout flow',
        inProgress: 'Mobile optimization, performance testing',
        firstDraft: '2024-09-01',
        secondDraft: '2024-09-15',
        finalSubmission: '2024-11-01',
        nextMilestone: '2024-11-15',
        createdAt: new Date('2024-08-01'),
        updatedAt: new Date('2024-10-20')
      },
      {
        id: 'dummy-2',
        projectNumber: 2,
        projectName: 'Brand Identity & Website',
        client: clients[1],
        email: emails[1],
        serviceType: services[1],
        status: statuses[2],
        completion: 85,
        version: '1.5.0',
        projectType: 'Standard',
        priority: 'Medium',
        active: true,
        invoiceNumber: 1002,
        invoiceDate: '2024-09-20',
        invoiceTotal: 8000,
        fiftyPercentCost: 4000,
        fiftyPercentPaid: 'Yes',
        tax: 1600,
        devCost: 3000,
        netProfit: 3400,
        clientPaid: 8000,
        remainingToBePaid: 0,
        paidToDev: 3000,
        extraDevCost: 0,
        shopifyThemeId: '187271971158',
        shopifyStoreDomain: 'techstart.myshopify.com',
        shopifyStoreUrl: 'https://techstart.myshopify.com/',
        nextSteps: 'Client review and feedback',
        completed: 'Logo design, brand guidelines, homepage mockup',
        inProgress: 'Responsive design implementation',
        firstDraft: '2024-09-10',
        secondDraft: '2024-09-25',
        finalSubmission: '2024-10-25',
        nextMilestone: '2024-10-30',
        createdAt: new Date('2024-08-15'),
        updatedAt: new Date('2024-10-18')
      },
      {
        id: 'dummy-3',
        projectNumber: 3,
        projectName: 'SEO Optimization Campaign',
        client: clients[2],
        email: emails[2],
        serviceType: services[2],
        status: statuses[0],
        completion: 10,
        version: '1.0.0',
        projectType: 'Maintenance',
        priority: 'Low',
        active: true,
        invoiceNumber: 1003,
        invoiceDate: '2024-11-01',
        invoiceTotal: 5000,
        fiftyPercentCost: 2500,
        fiftyPercentPaid: 'No',
        tax: 1000,
        devCost: 1500,
        netProfit: 1000,
        clientPaid: 0,
        remainingToBePaid: 5000,
        paidToDev: 0,
        extraDevCost: 0,
        shopifyThemeId: '187271971158',
        shopifyStoreDomain: 'retail.myshopify.com',
        shopifyStoreUrl: 'https://retail.myshopify.com/',
        nextSteps: 'Initial keyword research and analysis',
        completed: 'Project setup',
        inProgress: 'Competitor analysis',
        firstDraft: null,
        secondDraft: null,
        finalSubmission: '2024-12-31',
        nextMilestone: '2024-11-15',
        createdAt: new Date('2024-10-01'),
        updatedAt: new Date('2024-10-01')
      },
      {
        id: 'dummy-4',
        projectNumber: 4,
        projectName: 'Marketing Automation Setup',
        client: clients[3],
        email: emails[3],
        serviceType: services[3],
        status: statuses[4],
        completion: 30,
        version: '1.0.0',
        projectType: 'Custom',
        priority: 'Medium',
        active: false,
        invoiceNumber: 1004,
        invoiceDate: '2024-08-15',
        invoiceTotal: 12000,
        fiftyPercentCost: 6000,
        fiftyPercentPaid: 'Yes',
        tax: 2400,
        devCost: 4000,
        netProfit: 3600,
        clientPaid: 6000,
        remainingToBePaid: 6000,
        paidToDev: 2000,
        extraDevCost: 0,
        shopifyThemeId: '187271971158',
        shopifyStoreDomain: 'digital.myshopify.com',
        shopifyStoreUrl: 'https://digital.myshopify.com/',
        nextSteps: 'Awaiting client approval to proceed',
        completed: 'Initial setup and configuration',
        inProgress: null,
        firstDraft: '2024-08-20',
        secondDraft: null,
        finalSubmission: null,
        nextMilestone: '2024-11-20',
        createdAt: new Date('2024-07-01'),
        updatedAt: new Date('2024-09-01')
      },
      {
        id: 'dummy-5',
        projectNumber: 5,
        projectName: 'Shopify Store Migration',
        client: clients[4],
        email: emails[4],
        serviceType: services[0],
        status: statuses[3],
        completion: 100,
        version: '3.0.0',
        projectType: 'Rush',
        priority: 'Critical',
        active: true,
        invoiceNumber: 1005,
        invoiceDate: '2024-06-01',
        invoiceTotal: 20000,
        fiftyPercentCost: 10000,
        fiftyPercentPaid: 'Yes',
        tax: 4000,
        devCost: 8000,
        netProfit: 8000,
        clientPaid: 20000,
        remainingToBePaid: 0,
        paidToDev: 8000,
        extraDevCost: 2000,
        shopifyThemeId: '187271971158',
        shopifyStoreDomain: 'ecommerce.myshopify.com',
        shopifyStoreUrl: 'https://ecommerce.myshopify.com/',
        nextSteps: 'Project completed',
        completed: 'Full migration, theme customization, all integrations',
        inProgress: null,
        firstDraft: '2024-05-15',
        secondDraft: '2024-05-25',
        finalSubmission: '2024-06-05',
        nextMilestone: null,
        createdAt: new Date('2024-04-01'),
        updatedAt: new Date('2024-06-05')
      }
    ];
  }
  
  /**
   * Load dummy projects from localStorage or generate initial set
   */
  loadDummyProjects() {
    try {
      const stored = localStorage.getItem(this.dummyDataStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert date strings back to Date objects
        return parsed.map(p => ({
          ...p,
          createdAt: p.createdAt ? new Date(p.createdAt) : new Date(),
          updatedAt: p.updatedAt ? new Date(p.updatedAt) : new Date()
        }));
      }
    } catch (error) {
      console.warn('Failed to load dummy projects from localStorage:', error);
    }
    
    // Generate initial dummy data if nothing stored
    return this.generateDummyProjects();
  }
  
  /**
   * Save dummy projects to localStorage
   */
  saveDummyProjects(projects) {
    try {
      localStorage.setItem(this.dummyDataStorageKey, JSON.stringify(projects));
    } catch (error) {
      console.warn('Failed to save dummy projects to localStorage:', error);
    }
  }
  
  /**
   * Restore financial data to projects with missing financial info
   */
  restoreFinancialData() {
    console.log('💰 Restoring financial data to projects...');
    const projects = this.loadDummyProjects();
    let restoredCount = 0;
    
    const updatedProjects = projects.map((project, index) => {
      // Check if project has no financial data
      const hasNoFinancialData = 
        (!project.invoiceTotal || project.invoiceTotal === 0) &&
        (!project.clientPaid || project.clientPaid === 0);
      
      if (hasNoFinancialData) {
        restoredCount++;
        const invoiceTotal = 10000 + (index * 5000); // Varying amounts
        const clientPaid = invoiceTotal * 0.8; // 80% paid
        const tax = invoiceTotal * 0.2; // 20% tax
        const devCost = invoiceTotal * 0.4; // 40% dev cost
        const remainingToBePaid = invoiceTotal - clientPaid;
        const netProfit = invoiceTotal - tax - devCost;
        
        console.log(`✅ Restoring financial data for: ${project.projectName}`);
        
        return {
          ...project,
          invoiceNumber: 1000 + index,
          invoiceDate: '2024-10-15',
          invoiceTotal: invoiceTotal,
          fiftyPercentCost: invoiceTotal * 0.5,
          fiftyPercentPaid: clientPaid >= (invoiceTotal * 0.5) ? 'Yes' : 'No',
          tax: tax,
          devCost: devCost,
          netProfit: netProfit,
          clientPaid: clientPaid,
          remainingToBePaid: remainingToBePaid,
          paidToDev: devCost * 0.9,
          extraDevCost: 0,
          updatedAt: new Date()
        };
      }
      
      return project;
    });
    
    if (restoredCount > 0) {
      console.log(`💾 Restored financial data for ${restoredCount} project(s)`);
      this.saveDummyProjects(updatedProjects);
      return updatedProjects;
    }
    
    console.log('✅ All projects already have financial data');
    return projects;
  }
  
  /**
   * Create a new project in dummy data
   */
  createDummyProject(projectData) {
    const projects = this.loadDummyProjects();
    const newProject = {
      id: `dummy-${Date.now()}`,
      ...projectData,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    projects.push(newProject);
    this.saveDummyProjects(projects);
    return newProject;
  }
  
  /**
   * Update a project in dummy data
   */
  updateDummyProject(projectId, projectData) {
    const projects = this.loadDummyProjects();
    const index = projects.findIndex(p => p.id === projectId);
    if (index !== -1) {
      projects[index] = {
        ...projects[index],
        ...projectData,
        updatedAt: new Date()
      };
      this.saveDummyProjects(projects);
      return projects[index];
    }
    throw new Error('Project not found');
  }
  
  /**
   * Delete a project from dummy data
   */
  deleteDummyProject(projectId) {
    const projects = this.loadDummyProjects();
    const filtered = projects.filter(p => p.id !== projectId);
    this.saveDummyProjects(filtered);
    return true;
  }
  
  /**
   * Get statistics from dummy projects
   */
  getDummyStatistics() {
    const projects = this.loadDummyProjects();
    const total = projects.length;
    const active = projects.filter(p => p.active && p.status !== 'Completed').length;
    const completed = projects.filter(p => p.status === 'Completed').length;
    const overdue = projects.filter(p => {
      if (!p.nextMilestone) return false;
      const milestone = new Date(p.nextMilestone);
      const today = new Date();
      return milestone < today && p.status !== 'Completed';
    }).length;
    
    return { total, active, completed, overdue };
  }

  init() {
    // Log current data source mode
    if (this.useFirebase) {
      console.log('🔥 Firebase mode: Using Firebase database');
    } else {
      console.log('📝 Dummy data mode: Using local dummy data for development');
      console.log('💡 To enable Firebase, set "useFirebase: true" in section settings');
    }
    
    this.bindEvents();
    this.initNavbar();
    this.loadProjects();
    this.loadExpenses();
    this.initProgressSlider();
  }

  bindEvents() {
    // Navbar events
    document.getElementById('nav-menu-toggle')?.addEventListener('click', () => this.toggleMobileMenu());
    // Edit mode toggle
    document.getElementById('toggle-edit-mode-btn')?.addEventListener('click', () => this.toggleEditMode());
    
    // Button events
    document.getElementById('new-project-btn')?.addEventListener('click', () => this.openProjectModal());
    document.getElementById('finance-view-btn')?.addEventListener('click', () => this.toggleFinanceView());
    document.getElementById('sync-firebase-btn')?.addEventListener('click', () => this.syncToFirebase());
    document.getElementById('export-data-btn')?.addEventListener('click', () => this.exportData());
    document.getElementById('refresh-data-btn')?.addEventListener('click', () => this.loadProjects());
    
    // Tracker button events
    document.getElementById('export-tracker-btn')?.addEventListener('click', () => this.exportTrackerCSV());
    document.getElementById('refresh-tracker-btn')?.addEventListener('click', () => this.loadTrackerData());
    document.getElementById('clear-tracker-filters')?.addEventListener('click', () => this.clearTrackerFilters());
    
    // Expense button events
    document.getElementById('new-expense-btn')?.addEventListener('click', () => this.openExpenseModal());
    document.getElementById('export-expenses-btn')?.addEventListener('click', () => this.exportExpenses());
    document.getElementById('close-expense-modal')?.addEventListener('click', () => this.closeExpenseModal());
    document.getElementById('cancel-expense-form')?.addEventListener('click', () => this.closeExpenseModal());
    document.getElementById('expense-form')?.addEventListener('submit', (e) => this.handleExpenseFormSubmit(e));
    
    // Expense table actions - using event delegation
    const expenseTableBody = document.getElementById('expenses-table-body');
    if (expenseTableBody) {
      expenseTableBody.addEventListener('click', (e) => {
        const btn = e.target.closest('.expense-action-btn');
        if (!btn) return;

        e.preventDefault();
        e.stopPropagation();
        
        const action = btn.getAttribute('data-action');
        const expenseId = btn.getAttribute('data-expense-id');
        
        if (action === 'edit-expense') {
          this.editExpense(expenseId);
        } else if (action === 'delete-expense') {
          this.deleteExpense(expenseId);
        }
      });
    }
    
    // Modal events
    document.getElementById('close-modal')?.addEventListener('click', () => this.closeModal());
    document.getElementById('cancel-form')?.addEventListener('click', () => this.closeModal());
    document.getElementById('project-form')?.addEventListener('submit', (e) => this.handleFormSubmit(e));
    
    // Save Project buttons in each tab
    document.querySelectorAll('.save-project-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const form = document.getElementById('project-form');
        if (form) {
          // Trigger form submission
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(submitEvent);
        }
      });
    });
    
    // Cancel Project buttons in each tab
    document.querySelectorAll('.cancel-project-btn').forEach(btn => {
      btn.addEventListener('click', () => this.closeModal());
    });
    
    // Filter events
    document.getElementById('search-projects')?.addEventListener('input', (e) => this.filterProjects());
    document.getElementById('status-filter')?.addEventListener('change', () => this.filterProjects());
    document.getElementById('service-filter')?.addEventListener('change', () => this.filterProjects());
    document.getElementById('clear-filters')?.addEventListener('click', () => this.clearFilters());
    
    // Tracker filter events
    document.getElementById('tracker-search')?.addEventListener('input', () => this.filterTrackerProjects());
    document.getElementById('tracker-status-filter')?.addEventListener('change', () => this.filterTrackerProjects());
    document.getElementById('tracker-service-filter')?.addEventListener('change', () => this.filterTrackerProjects());
    document.getElementById('tracker-payment-filter')?.addEventListener('change', () => this.filterTrackerProjects());
    
    // Modal backdrop click
    document.getElementById('project-modal')?.addEventListener('click', (e) => {
      if (e.target.id === 'project-modal') {
        this.closeModal();
      }
    });
    
    // Expense modal backdrop click
    document.getElementById('expense-modal')?.addEventListener('click', (e) => {
      if (e.target.id === 'expense-modal') {
        this.closeExpenseModal();
      }
    });

    // Form interaction events
    this.initFormInteractions();
    
    // Form tab events
    this.initFormTabs();
    
    // Dashboard tab events
    this.initDashboardTabs();
    
    // Sorting events
    this.initSorting();
    
    // Event delegation for project action buttons (Edit/Delete)
    const handleActionClick = (e) => {
      const btn = e.target.closest('.project-action-btn');
      
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        
        const action = btn.getAttribute('data-action');
        const projectId = btn.getAttribute('data-project-id');
        
        if (projectId && projectId.trim() !== '') {
          if (action === 'edit') {
            this.editProject(projectId);
          } else if (action === 'delete') {
            this.deleteProject(projectId);
          }
        }
      }
    };
    
    // Add listener to document
    document.addEventListener('click', handleActionClick.bind(this), true);
  }

  initFormInteractions() {
    // Initialize floating labels
    const formInputs = document.querySelectorAll('.form-input, .form-select, .form-textarea');
    formInputs.forEach(input => {
      const formGroup = input.closest('.form-group');
      
      // Check initial state
      this.updateFloatingLabel(input, formGroup);
      
      // Focus events
      input.addEventListener('focus', () => {
        formGroup?.classList.add('focused');
      });
      
      input.addEventListener('blur', () => {
        formGroup?.classList.remove('focused');
        this.updateFloatingLabel(input, formGroup);
      });
      
      // Input events
      input.addEventListener('input', () => {
        this.updateFloatingLabel(input, formGroup);
      });
      
      // Change events for selects
      input.addEventListener('change', () => {
        this.updateFloatingLabel(input, formGroup);
      });
    });

    // Enhanced progress slider
    this.initProgressSliderEnhanced();
  }

  initFormTabs() {
    // Form tab switching
    const formTabButtons = document.querySelectorAll('.form-tab-btn');
    const formTabContents = document.querySelectorAll('.form-tab-content');

    formTabButtons.forEach(button => {
      button.addEventListener('click', () => {
        const targetTab = button.dataset.tab;

        // Update button states
        formTabButtons.forEach(btn => {
          btn.classList.remove('bg-blue-600', 'text-white', 'active');
          btn.classList.add('text-gray-400', 'hover:text-white', 'hover:bg-gray-600');
        });
        
        button.classList.add('bg-blue-600', 'text-white', 'active');
        button.classList.remove('text-gray-400', 'hover:text-white', 'hover:bg-gray-600');

        // Update tab content
        formTabContents.forEach(content => {
          if (content.id === `${targetTab}-tab-content`) {
            content.classList.remove('hidden');
          } else {
            content.classList.add('hidden');
          }
        });
      });
    });
  }

  initDashboardTabs() {
    // Dashboard tab switching
    const dashboardTabButtons = document.querySelectorAll('.dashboard-tab-btn');
    const dashboardTabContents = document.querySelectorAll('.dashboard-tab-content');

    dashboardTabButtons.forEach(button => {
      button.addEventListener('click', () => {
        const targetTab = button.dataset.tab;

        // Update button states
        dashboardTabButtons.forEach(btn => {
          btn.classList.remove('bg-indigo-600', 'text-white', 'active');
          btn.classList.add('text-gray-400', 'hover:text-white', 'hover:bg-gray-600');
        });
        
        button.classList.add('bg-indigo-600', 'text-white', 'active');
        button.classList.remove('text-gray-400', 'hover:text-white', 'hover:bg-gray-600');

        // Update tab content
        dashboardTabContents.forEach(content => {
          if (content.id === `${targetTab}-dashboard-content`) {
            content.classList.remove('hidden');
          } else {
            content.classList.add('hidden');
          }
        });

        // Load finance data when finance tab is opened
        if (targetTab === 'finance') {
          this.loadFinanceData();
        }
        
        // Load tracker data when tracker tab is opened
        if (targetTab === 'tracker') {
          this.loadTrackerData();
        }
      });
    });
  }

  updateFloatingLabel(input, formGroup) {
    if (!formGroup) return;
    
    const hasValue = input.value && input.value.trim() !== '';
    if (hasValue) {
      formGroup.classList.add('has-value');
    } else {
      formGroup.classList.remove('has-value');
    }
  }

  initProgressSliderEnhanced() {
    const container = document.getElementById('progress-bar-container');
    const handle = document.getElementById('modal-progress-drag-handle');
    const valueDisplay = document.getElementById('completion-value');
    const progressFill = document.getElementById('progress-fill');
    const hiddenInput = document.getElementById('completion');
    
    if (!container || !handle || !valueDisplay || !progressFill || !hiddenInput) return;

    let isDragging = false;

    const updateProgress = (percentage) => {
      percentage = Math.max(0, Math.min(100, percentage));
      
      // Update displays
      valueDisplay.textContent = percentage + '%';
      progressFill.style.width = percentage + '%';
      handle.style.left = percentage + '%';
      hiddenInput.value = percentage;
    };

    const getPercentageFromEvent = (e) => {
      const rect = container.getBoundingClientRect();
      const x = (e.clientX || e.touches[0].clientX) - rect.left;
      return Math.round((x / rect.width) * 100);
    };

    // Mouse events
    handle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      isDragging = true;
      handle.style.cursor = 'grabbing';
    });

    container.addEventListener('click', (e) => {
      if (!isDragging) {
        const percentage = getPercentageFromEvent(e);
        updateProgress(percentage);
      }
    });

    document.addEventListener('mousemove', (e) => {
      if (isDragging) {
        const percentage = getPercentageFromEvent(e);
        updateProgress(percentage);
      }
    });

    document.addEventListener('mouseup', () => {
      if (isDragging) {
        isDragging = false;
        handle.style.cursor = 'grab';
      }
    });

    // Touch events for mobile
    handle.addEventListener('touchstart', (e) => {
      e.preventDefault();
      isDragging = true;
    });

    document.addEventListener('touchmove', (e) => {
      if (isDragging) {
        const percentage = getPercentageFromEvent(e);
        updateProgress(percentage);
      }
    });

    document.addEventListener('touchend', () => {
      isDragging = false;
    });

    // Initialize
    updateProgress(parseInt(hiddenInput.value) || 0);
  }

  initSorting() {
    // Add click events to sortable headers
    const sortableHeaders = document.querySelectorAll('th[data-sort]');
    sortableHeaders.forEach(header => {
      header.addEventListener('click', () => {
        const field = header.dataset.sort;
        this.sortProjects(field);
      });
    });
  }

  sortProjects(field) {
    // Toggle direction if same field, otherwise default to ascending
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    // Update visual indicators
    this.updateSortIndicators();

    // Sort the filtered projects
    this.filteredProjects.sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      // Handle different data types
      if (field === 'projectNumber') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      } else if (field === 'completion') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      } else {
        // String comparison
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }

      if (this.sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    // Re-render the table
    this.renderProjectsTable();
  }

  updateSortIndicators() {
    // Remove all active sort indicators
    document.querySelectorAll('th[data-sort] .sort-icon').forEach(icon => {
      icon.classList.remove('text-blue-600', 'transform', 'rotate-180');
      icon.classList.add('text-gray-400');
    });

    // Add active indicator to current sort field
    const activeHeader = document.querySelector(`th[data-sort="${this.sortField}"] .sort-icon`);
    if (activeHeader) {
      activeHeader.classList.remove('text-gray-400');
      activeHeader.classList.add('text-blue-600');
      
      if (this.sortDirection === 'desc') {
        activeHeader.classList.add('transform', 'rotate-180');
      }
    }
  }

  initProgressSlider() {
    const slider = document.getElementById('completion');
    const valueDisplay = document.getElementById('completion-value');
    
    if (slider && valueDisplay) {
      slider.addEventListener('input', (e) => {
        valueDisplay.textContent = e.target.value + '%';
      });
    }
  }


  initNavbar() {
    const navbar = document.querySelector('.admin-navbar');
    if (!navbar) return;

    // Handle scroll effects
    let lastScrollTop = 0;
    const scrollThreshold = 10;

    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Add scrolled class for styling
      if (scrollTop > scrollThreshold) {
        navbar.classList.add('scrolled');
      } else {
        navbar.classList.remove('scrolled');
      }
      
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    });

    // Update last updated time
    this.updateLastUpdatedTime();
  }

  toggleMobileMenu() {
    const mobileMenu = document.querySelector('.mobile-nav-menu');
    if (mobileMenu) {
      mobileMenu.classList.toggle('active');
    }
  }

  updateLastUpdatedTime() {
    const timeElement = document.getElementById('last-updated-time');
    if (timeElement) {
      const now = new Date();
      const timeString = now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      timeElement.textContent = timeString;
    }
  }

  toggleEditMode() {
    this.isEditMode = !this.isEditMode;
    const btn = document.getElementById('toggle-edit-mode-btn');
    if (btn) {
      btn.textContent = this.isEditMode ? 'Edit Mode: On' : 'Edit Mode: Off';
      btn.classList.toggle('bg-green-700', this.isEditMode);
      btn.classList.toggle('bg-slate-800', !this.isEditMode);
    }
    // Add/remove edit mode class to table for CSS targeting
    const table = document.querySelector('#projects-table-content table');
    if (table) {
      if (this.isEditMode) {
        table.classList.add('edit-mode-active');
      } else {
        table.classList.remove('edit-mode-active');
      }
    }
    this.renderProjectsTable();
  }

  applyInlineEditing() {
    // Keep actions column visible in edit mode - users may still want to edit or delete
    const tbody = document.getElementById('projects-table-body');
    if (!tbody) return;
    // Remove previous handlers by cloning (simple way to drop listeners)
    const newTbody = tbody.cloneNode(true);
    tbody.parentNode.replaceChild(newTbody, tbody);
    // Bind client email popups (always active) - copy + toast
    newTbody.querySelectorAll('.client-email-link').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const email = e.currentTarget.getAttribute('data-email') || '';
        if (!email) {
          this.showWarning('No email on record for this client.');
          return;
        }
        try { navigator.clipboard?.writeText(email); } catch {}
        this.showNotification(`Email: ${this.escapeHtml(email)} (copied)`, 'info');
      });
    });
    // Timeline is not editable - removed drag handlers binding
    if (!this.isEditMode) return;
    const handleCellClick = (e) => {
      const container = e.target.closest('[data-editable="true"]');
      if (!container) return;
      const field = container.getAttribute('data-field');
      const projectId = container.getAttribute('data-id');
      if (!field || !projectId) return;
      // Avoid opening editor if one already exists
      if (container.querySelector('.inline-editor')) return;
      if (field === 'status') {
        const select = document.createElement('select');
        select.className = 'inline-editor px-2 py-1 text-sm bg-gray-800 border-2 border-blue-500 rounded text-white focus:border-blue-400 focus:outline-none';
        this.STATUS_OPTIONS.forEach(opt => {
          const o = document.createElement('option');
          o.value = opt; o.textContent = opt;
          o.className = 'bg-gray-800 text-white';
          if (container.innerText.trim().startsWith(opt)) o.selected = true;
          select.appendChild(o);
        });
        select.onchange = async () => {
          const value = select.value;
          await this.inlineUpdate(projectId, 'status', value);
          // Update the parent td's background color class dynamically
          const parentTd = container.closest('td');
          if (parentTd) {
            // Remove old status background classes (include all possible colors: blue, green, orange, red, gray)
            const classes = parentTd.className.split(' ').filter(c => 
              !c.match(/^bg-(blue|green|orange|red|gray|grey|slate|emerald|amber|yellow|cyan|purple)-\d+$/)
            );
            // Add new status background class
            classes.push(this.getStatusBg(value));
            parentTd.className = classes.join(' ').trim();
          }
          container.innerHTML = `<span class="text-white">${this.escapeHtml(value)}</span>`;
        };
        select.onblur = select.onchange;
        container.innerHTML = '';
        container.appendChild(select);
        select.focus();
        return;
      }
      if (field === 'active') {
        const select = document.createElement('select');
        select.className = 'inline-editor px-2 py-1 text-sm bg-gray-800 border-2 border-blue-500 rounded text-white focus:border-blue-400 focus:outline-none';
        ['Yes','No'].forEach(opt => {
          const o = document.createElement('option');
          o.value = opt; o.textContent = opt;
          o.className = 'bg-gray-800 text-white';
          if (container.innerText.trim().startsWith(opt)) o.selected = true;
          select.appendChild(o);
        });
        select.onchange = async () => {
          const value = select.value === 'Yes';
          await this.inlineUpdate(projectId, 'active', value);
          container.innerHTML = `<span class="text-white">${value ? 'Yes' : 'No'}</span>`;
        };
        select.onblur = select.onchange;
        container.innerHTML = '';
        container.appendChild(select);
        select.focus();
        return;
      }
      // Text areas for longer fields
      if (['nextSteps','completed','inProgress'].includes(field)) {
        const current = container.innerText.trim() === '-' ? '' : container.innerText.trim();
        const textarea = document.createElement('textarea');
        textarea.className = 'inline-editor w-full min-h-[2.25rem] text-sm bg-gray-800 border-2 border-blue-500 rounded px-2 py-1 text-white focus:border-blue-400 focus:outline-none placeholder-gray-400';
        textarea.value = current;
        const save = async () => {
          const value = textarea.value.trim();
          await this.inlineUpdate(projectId, field, value);
          container.innerHTML = `<span class="text-white">${this.escapeHtml(value || '-')}</span>`;
        };
        textarea.onblur = save;
        textarea.onkeydown = (ke) => {
          if (ke.key === 'Enter' && (ke.metaKey || ke.ctrlKey)) {
            textarea.blur();
          }
        };
        container.innerHTML = '';
        container.appendChild(textarea);
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = textarea.value.length;
      }
    };
    newTbody.addEventListener('click', handleCellClick);
  }

  async inlineUpdate(projectId, field, value) {
    const project = this.projects.find(p => p.id === projectId);
    if (!project) return;
    // Basic normalization
    const numericFields = ['completion'];
    if (numericFields.includes(field)) {
      const num = parseInt(value, 10);
      if (!Number.isNaN(num)) project[field] = num;
    } else {
      project[field] = value;
    }
    try {
      if (this.useFirebase && this.firebaseManager) {
        await this.firebaseManager.ensureInitialized();
        await this.firebaseManager.updateProject(projectId, { [field]: project[field] });
      } else {
        this.updateDummyProject(projectId, { [field]: project[field] });
      }
      
      // Update UI dynamically based on field type
      if (field === 'completion') {
        // Update timeline progress bar
        this.updateTimelineCell(projectId);
      }
      
      this.showSuccess('Updated');
    } catch (err) {
      console.error(err);
      this.showError('Failed to save');
    }
  }

  updateFinanceStats() {
    const totalRevenue = this.projects.reduce((sum, p) => sum + (parseFloat(p.invoiceTotal) || 0), 0);
    const totalProfit = this.projects.reduce((sum, p) => sum + (parseFloat(p.netProfit) || 0), 0);
    const totalOutstanding = this.projects.reduce((sum, p) => sum + (parseFloat(p.remainingToBePaid) || 0), 0);
    const totalPaidDevs = this.projects.reduce((sum, p) => sum + (parseFloat(p.paidToDev) || 0), 0);

    document.getElementById('total-revenue').textContent = `£${totalRevenue.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('total-profit').textContent = `£${totalProfit.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('total-outstanding').textContent = `£${totalOutstanding.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('total-paid-devs').textContent = `£${totalPaidDevs.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  }

  loadFinanceData() {
    // Debug: Check if projects exist and have financial data
    console.log('💰 Loading finance data...');
    console.log('Total projects:', this.projects.length);
    console.log('Sample project data:', this.projects[0]);
    
    // Update finance dashboard cards - Financial metrics
    const projectedRevenue = this.projects.reduce((sum, p) => sum + (parseFloat(p.invoiceTotal) || 0), 0);
    const projectedRevenue50 = projectedRevenue * 0.5;
    const totalPaid = this.projects.reduce((sum, p) => sum + (parseFloat(p.clientPaid) || 0), 0);
    const totalOutstanding = this.projects.reduce((sum, p) => sum + (parseFloat(p.remainingToBePaid) || 0), 0);
    
    // Update finance dashboard cards - Project counts
    const activeProjects = this.projects.filter(p => p.active && p.status !== 'Completed').length;
    const projectsPaid50 = this.projects.filter(p => p.fiftyPercentPaid === true || p.fiftyPercentPaid === 'true' || p.fiftyPercentPaid === 1).length;
    const projectsPaidFull = this.projects.filter(p => (parseFloat(p.remainingToBePaid) || 0) === 0 && (parseFloat(p.invoiceTotal) || 0) > 0).length;
    const projectsCompleted = this.projects.filter(p => p.status === 'Completed').length;
    
    console.log('Calculated totals:', { projectedRevenue, projectedRevenue50, totalPaid, totalOutstanding });
    console.log('Project counts:', { activeProjects, projectsPaid50, projectsPaidFull, projectsCompleted });

    // Update financial metrics
    document.getElementById('finance-projected-revenue').textContent = `£${projectedRevenue.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('finance-50-projected-revenue').textContent = `£${projectedRevenue50.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('finance-total-paid').textContent = `£${totalPaid.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('finance-outstanding').textContent = `£${totalOutstanding.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;

    // Update project count metrics
    document.getElementById('finance-active-projects').textContent = activeProjects;
    document.getElementById('finance-paid-50').textContent = projectsPaid50;
    document.getElementById('finance-paid-full').textContent = projectsPaidFull;
    document.getElementById('finance-completed-projects').textContent = projectsCompleted;

    // Create charts and Excel style table
    this.createRevenueChart();
    this.populateFinanceTable();
    this.populateExcelStyleTable();
  }

  createRevenueChart() {
    const ctx = document.getElementById('revenueChart');
    if (!ctx) return;

    // Destroy existing chart if it exists
    if (this.revenueChart) {
      this.revenueChart.destroy();
    }

    // Calculate totals across all projects
    const totalInvoicesSent = this.projects.reduce((sum, p) => sum + (parseFloat(p.invoiceTotal) || 0), 0);
    const totalInvoicesWon = this.projects.reduce((sum, p) => sum + (parseFloat(p.invoiceTotal) || 0), 0);
    const totalPaid = this.projects.reduce((sum, p) => sum + (parseFloat(p.clientPaid) || 0), 0);
    const totalTax = this.projects.reduce((sum, p) => sum + (parseFloat(p.tax) || 0), 0);
    const totalDevCost = this.projects.reduce((sum, p) => sum + (parseFloat(p.devCost) || 0), 0);
    const totalRemaining = this.projects.reduce((sum, p) => sum + (parseFloat(p.remainingToBePaid) || 0), 0);
    const totalNetProfit = this.projects.reduce((sum, p) => sum + (parseFloat(p.netProfit) || 0), 0);

    // Calculate 50% values for stacked bars
    const invoicesSent50Percent = totalInvoicesSent * 0.5;
    const invoicesWon50Percent = totalInvoicesWon * 0.5;
    
    console.log('📊 Chart data:', {
      totalInvoicesSent,
      totalInvoicesWon,
      totalPaid,
      totalTax,
      totalDevCost,
      totalRemaining,
      totalNetProfit
    });

    // Store totals for use in table
    this.financeTotals = {
      totalInvoicesSent,
      totalInvoicesWon,
      totalPaid,
      totalTax,
      totalDevCost,
      totalRemaining,
      totalNetProfit
    };

    this.revenueChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: [
          'Invoices Sent',
          'Invoices Won',
          'Paid',
          'Tax (20%)',
          'Dev Cost',
          'Remaining To Paid',
          'Net Profit'
        ],
        datasets: [
          // First 50% segment for stacked bars (Invoices Sent)
          {
            label: '50%',
            data: [invoicesSent50Percent, 0, 0, 0, 0, 0, 0],
            backgroundColor: '#22C55E', // Green-500 for first 50%
            borderColor: '#16A34A', // Green-600 border
            borderWidth: 1,
          },
          // Second 50% segment for Invoices Sent
          {
            label: '50%',
            data: [invoicesSent50Percent, 0, 0, 0, 0, 0, 0],
            backgroundColor: '#3B82F6', // Blue-500 for second 50%
            borderColor: '#2563EB', // Blue-600 border
            borderWidth: 1,
          },
          // First 50% segment for stacked bars (Invoices Won)
          {
            label: '50%',
            data: [0, invoicesWon50Percent, 0, 0, 0, 0, 0],
            backgroundColor: '#22C55E', // Green-500 for first 50%
            borderColor: '#16A34A', // Green-600 border
            borderWidth: 1,
          },
          // Second 50% segment for Invoices Won
          {
            label: '50%',
            data: [0, invoicesWon50Percent, 0, 0, 0, 0, 0],
            backgroundColor: '#3B82F6', // Blue-500 for second 50%
            borderColor: '#2563EB', // Blue-600 border
            borderWidth: 1,
          },
          // Regular bars for other metrics (each in its own stack)
          {
            label: 'Total Paid',
            data: [0, 0, totalPaid, 0, 0, 0, 0],
            backgroundColor: 'rgba(251, 191, 36, 0.8)', // Yellow
            borderColor: 'rgba(251, 191, 36, 1)',
            borderWidth: 1,
          },
          {
            label: 'Tax',
            data: [0, 0, 0, totalTax, 0, 0, 0],
            backgroundColor: 'rgba(168, 85, 247, 0.8)', // Purple
            borderColor: 'rgba(168, 85, 247, 1)',
            borderWidth: 1,
          },
          {
            label: 'Dev Cost',
            data: [0, 0, 0, 0, totalDevCost, 0, 0],
            backgroundColor: 'rgba(239, 68, 68, 0.8)', // Red
            borderColor: 'rgba(239, 68, 68, 1)',
            borderWidth: 1,
          },
          {
            label: 'Remaining',
            data: [0, 0, 0, 0, 0, totalRemaining, 0],
            backgroundColor: 'rgba(251, 191, 36, 0.6)', // Light yellow
            borderColor: 'rgba(251, 191, 36, 1)',
            borderWidth: 1,
          },
          {
            label: 'Net Profit',
            data: [0, 0, 0, 0, 0, 0, totalNetProfit],
            backgroundColor: 'rgba(34, 197, 94, 0.8)', // Green
          borderColor: 'rgba(34, 197, 94, 1)',
            borderWidth: 1,
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'category',
            stacked: true,
            offset: true,
            barPercentage: 0.9,
            categoryPercentage: 0.9,
            grid: {
              offset: false  // important: keeps grid lines aligned
            }
          },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return '£' + value.toLocaleString();
              },
              color: 'white'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          }
        },
        plugins: {
          tooltip: {
            enabled: false // Disable hover tooltips
          },
          legend: {
            display: false
          },
          datalabels: false // Disable default datalabels if plugin exists
        }
      },
      plugins: [{
        id: 'customLabels',
        afterDatasetsDraw: (chart) => {
          // Only draw labels after animation completes
          if (chart.animating) return;
          
          const ctx = chart.ctx;
          ctx.save();
          
          chart.data.datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex);
            if (!meta.visible) return;
            
            meta.data.forEach((bar, index) => {
              const value = dataset.data[index];
              if (!value || value === 0) return;
              
              // Prepare label text
              let labelText = '';
              let isStacked = false;
              
              // First 4 datasets are stacked bars (2 segments each)
              if (datasetIndex < 4) {
                isStacked = true;
                const isBottomSegment = datasetIndex % 2 === 0;
                
                if (isBottomSegment) {
                  labelText = '50%: £' + value.toLocaleString('en-GB');
                } else {
                  // Top segment: value * 2 since both segments are equal
                  labelText = '100%: £' + (value * 2).toLocaleString('en-GB');
                }
              } else {
                // Regular bars: just show the value
                labelText = '£' + value.toLocaleString('en-GB');
              }
              
              // Set font and measure text
              ctx.font = 'bold 11px sans-serif';
              const textWidth = ctx.measureText(labelText).width;
              const padding = 6;
              const boxWidth = textWidth + (padding * 2);
              const boxHeight = 20;
              
              // Calculate position - center of the segment
              const x = bar.x;
              const y = (bar.base + bar.y) / 2;
              
              // Draw background box with contrasting color
              ctx.fillStyle = 'rgba(0, 0, 0, 0.75)'; // Semi-transparent black for good contrast
              ctx.beginPath();
              ctx.roundRect(x - boxWidth/2, y - boxHeight/2, boxWidth, boxHeight, 4);
              ctx.fill();
              
              // Draw white border for better visibility
              ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
              ctx.lineWidth = 1;
              ctx.stroke();
              
              // Draw text
              ctx.fillStyle = '#FFFFFF';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(labelText, x, y);
            });
          });
          
          ctx.restore();
        }
      }]
    });
  }

  populateFinanceTable() {
    const tbody = document.getElementById('finance-table-body');
    const tfoot = document.getElementById('finance-table-totals');
    if (!tbody || !tfoot) return;

    const financeProjects = this.projects.filter(p => 
      parseFloat(p.invoiceTotal) > 0 || parseFloat(p.netProfit) > 0
    );

    // Calculate totals
    const totalInvoicesSent = this.projects.reduce((sum, p) => sum + (parseFloat(p.invoiceTotal) || 0), 0);
    const totalInvoicesWon = this.projects.reduce((sum, p) => sum + (parseFloat(p.invoiceTotal) || 0), 0);
    const totalPaid = this.projects.reduce((sum, p) => sum + (parseFloat(p.clientPaid) || 0), 0);
    const totalTax = this.projects.reduce((sum, p) => sum + (parseFloat(p.tax) || 0), 0);
    const totalDevCost = this.projects.reduce((sum, p) => sum + (parseFloat(p.devCost) || 0), 0);
    const totalRemaining = this.projects.reduce((sum, p) => sum + (parseFloat(p.remainingToBePaid) || 0), 0);
    const totalNetProfit = this.projects.reduce((sum, p) => sum + (parseFloat(p.netProfit) || 0), 0);

    // Populate table rows
    tbody.innerHTML = financeProjects.map(project => {
      const invoiceTotal = parseFloat(project.invoiceTotal) || 0;
      const invoiceWon = parseFloat(project.invoiceTotal) || 0; // Same as sent
      const clientPaid = parseFloat(project.clientPaid) || 0;
      const tax = parseFloat(project.tax) || 0;
      const devCost = parseFloat(project.devCost) || 0;
      const remaining = parseFloat(project.remainingToBePaid) || 0;
      const netProfit = parseFloat(project.netProfit) || 0;

      return `
        <tr>
          <td class="px-4 py-4 whitespace-nowrap text-center">
            <div class="text-sm font-medium text-white">${this.escapeHtml(project.projectName || 'Untitled')}</div>
            <div class="mt-1 text-xs italic text-gray-500">
              <button type="button" class="client-email-link underline decoration-dotted hover:text-gray-700" data-email="${this.escapeHtml(project.email || '')}" title="View email">
                ${this.escapeHtml(project.client || 'N/A')}
              </button>
            </div>
        </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            £${invoiceTotal.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            £${invoiceWon.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            £${clientPaid.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            £${tax.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            £${devCost.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            £${remaining.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white font-medium text-center">
            £${netProfit.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </td>
      </tr>
      `;
    }).join('');

    // Populate totals row
    tfoot.innerHTML = `
      <tr class="bg-gray-100 border-t-2 border-gray-300">
        <td class="px-4 py-4 whitespace-nowrap text-center">
          <span class="text-sm font-bold text-gray-900">TOTALS:</span>
        </td>
        <td class="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-center">
          £${totalInvoicesSent.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </td>
        <td class="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-center">
          £${totalInvoicesWon.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </td>
        <td class="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-center">
          £${totalPaid.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </td>
        <td class="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-center">
          £${totalTax.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </td>
        <td class="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-center">
          £${totalDevCost.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </td>
        <td class="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-center">
          £${totalRemaining.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </td>
        <td class="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-center">
          £${totalNetProfit.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </td>
      </tr>
    `;
    
    // Bind email link click handlers
    tbody.querySelectorAll('.client-email-link').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const email = e.currentTarget.getAttribute('data-email') || '';
        if (!email) {
          this.showWarning('No email on record for this client.');
          return;
        }
        try { navigator.clipboard?.writeText(email); } catch {}
        this.showNotification(`Email: ${this.escapeHtml(email)} (copied)`, 'info');
      });
    });
  }

  getPaymentStatusClass(project) {
    const outstanding = parseFloat(project.remainingToBePaid) || 0;
    const total = parseFloat(project.invoiceTotal) || 0;
    
    if (outstanding === 0) return 'bg-green-100 text-green-800';
    if (outstanding > 0 && outstanding < total) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  }

  getPaymentStatusText(project) {
    const outstanding = parseFloat(project.remainingToBePaid) || 0;
    const total = parseFloat(project.invoiceTotal) || 0;
    
    if (outstanding === 0) return 'Paid';
    if (outstanding > 0 && outstanding < total) return 'Partial';
    return 'Unpaid';
  }

  loadTrackerData() {
    // Add sample data if no projects exist
    if (this.projects.length === 0) {
      this.addSampleData();
    }
    this.populateTrackerTable();
    this.updateTrackerSummary();
  }

  addSampleData() {
    // Sample data based on your Excel sheet
    this.projects = [
      {
        id: '1',
        projectNumber: 1,
        projectName: 'CherryChutney Shopify Theme Creation',
        client: 'CherryChutney',
        email: 'info@cherrychutney.com',
        status: 'Awaiting Feedback',
        invoiceNumber: 31,
        invoiceDate: '2024-09-23',
        invoiceTotal: 1800.00,
        fiftyPercentCost: 900.00,
        fiftyPercentPaid: 'No',
        tax: 300.00,
        devCost: 600.00,
        netProfit: 900.00,
        clientPaid: 0.00,
        remainingToBePaid: 1800.00,
        paidToDev: 0.00,
        extraDevCost: 0.00,
        serviceType: 'Web Dev (Shopify)',
        active: true,
        completion: 75
      },
      {
        id: '2',
        projectNumber: 2,
        projectName: 'WM',
        client: 'WM Client',
        email: 'contact@wm.com',
        status: 'Completed',
        invoiceNumber: 35,
        invoiceDate: '2024-12-08',
        invoiceTotal: 3350.00,
        fiftyPercentCost: 1675.00,
        fiftyPercentPaid: 'Yes',
        tax: 558.33,
        devCost: 1890.00,
        netProfit: 901.67,
        clientPaid: 3350.00,
        remainingToBePaid: 0.00,
        paidToDev: 2040.00,
        extraDevCost: 0.00,
        serviceType: 'Web Dev (Shopify)',
        active: true,
        completion: 100
      },
      {
        id: '3',
        projectNumber: 3,
        projectName: 'Therapeutics 1.3 Settings',
        client: 'Therapeutics',
        email: 'info@therapeutics.com',
        status: 'Completed',
        invoiceNumber: 32,
        invoiceDate: '2024-10-15',
        invoiceTotal: 2500.00,
        fiftyPercentCost: 1250.00,
        fiftyPercentPaid: 'Yes',
        tax: 416.67,
        devCost: 1400.00,
        netProfit: 683.33,
        clientPaid: 2500.00,
        remainingToBePaid: 0.00,
        paidToDev: 1400.00,
        extraDevCost: 0.00,
        serviceType: 'Web Dev (Shopify)',
        active: true,
        completion: 100
      },
      {
        id: '4',
        projectNumber: 4,
        projectName: 'Sculp',
        client: 'Sculp',
        email: 'info@sculp.shop',
        status: 'In Progress (2nd Draft)',
        invoiceNumber: 33,
        invoiceDate: '2025-04-20',
        invoiceTotal: 5000.00,
        fiftyPercentCost: 2500.00,
        fiftyPercentPaid: 'No',
        tax: 833.33,
        devCost: 1375.00,
        netProfit: 2791.67,
        clientPaid: 1000.00,
        remainingToBePaid: 4000.00,
        paidToDev: 0.00,
        extraDevCost: 0.00,
        serviceType: 'Web Dev (Shopify)',
        active: true,
        completion: 90
      },
      {
        id: '5',
        projectNumber: 5,
        projectName: 'TherapyRugs',
        client: 'TherapyRugs',
        email: 'info@therapyrugs.com',
        status: 'Not Started',
        invoiceNumber: 34,
        invoiceDate: '2025-01-15',
        invoiceTotal: 3200.00,
        fiftyPercentCost: 1600.00,
        fiftyPercentPaid: 'No',
        tax: 533.33,
        devCost: 1800.00,
        netProfit: 866.67,
        clientPaid: 0.00,
        remainingToBePaid: 3200.00,
        paidToDev: 0.00,
        extraDevCost: 0.00,
        serviceType: 'Web Development',
        active: true,
        completion: 0
      }
    ];
    
    // Update filtered projects
    this.filteredProjects = [...this.projects];
    
    // Update statistics
    this.updateStatistics();
  }

  populateTrackerTable() {
    const tbody = document.getElementById('tracker-table-body');
    if (!tbody) return;

    // Sort projects by project number for consistent display
    const sortedProjects = [...this.projects].sort((a, b) => {
      const numA = parseInt(a.projectNumber) || 0;
      const numB = parseInt(b.projectNumber) || 0;
      return numA - numB;
    });

    tbody.innerHTML = sortedProjects.map((project, index) => {
      const invoiceTotal = parseFloat(project.invoiceTotal) || 0;
      const fiftyPercentCost = parseFloat(project.fiftyPercentCost) || 0;
      const tax = parseFloat(project.tax) || 0;
      const devCost = parseFloat(project.devCost) || 0;
      const netProfit = parseFloat(project.netProfit) || 0;
      const clientPaid = parseFloat(project.clientPaid) || 0;
      const remainingToBePaid = parseFloat(project.remainingToBePaid) || 0;
      const paidToDev = parseFloat(project.paidToDev) || 0;
      const extraDevCost = parseFloat(project.extraDevCost) || 0;
      
      const paymentStatus = this.getPaymentStatusText(project);
      
      return `
        <tr>
          <td class="px-3 py-4 whitespace-nowrap text-sm font-bold text-white text-center">
            ${project.projectNumber || (index + 1)}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-center">
            <div class="text-sm font-medium text-white">${this.escapeHtml(project.projectName || 'Untitled')}</div>
            <div class="mt-1 text-xs italic text-gray-500">
              <button type="button" class="client-email-link underline decoration-dotted hover:text-gray-700" data-email="${this.escapeHtml(project.email || '')}" title="View email">
                ${this.escapeHtml(project.client || 'N/A')}
              </button>
            </div>
          </td>
          <td class="px-6 py-0 ${this.getStatusBg(project.status)} text-center">
            <div class="w-full h-full min-h-[56px] flex items-center justify-center">
              <span class="text-sm font-semibold text-white whitespace-nowrap">${this.escapeHtml(project.status || 'Unknown')}</span>
            </div>
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            ${project.invoiceNumber || '-'}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            ${project.invoiceDate ? this.formatDate(project.invoiceDate) : '-'}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white font-medium text-center">
            £${invoiceTotal.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            £${fiftyPercentCost.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-center">
            <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full ${project.fiftyPercentPaid === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}">
              ${project.fiftyPercentPaid || 'No'}
            </span>
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            £${tax.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            £${devCost.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white font-medium text-center">
            £${netProfit.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            £${clientPaid.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            £${remainingToBePaid.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            £${paidToDev.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-4 whitespace-nowrap text-sm text-white text-center">
            £${extraDevCost.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
            <div class="flex items-center justify-center space-x-2">
              <button data-action="edit" data-project-id="${project.id || ''}" 
                      class="project-action-btn inline-flex items-center p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                      title="Edit Project">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </button>
              <button data-action="delete" data-project-id="${project.id || ''}" 
                      class="project-action-btn inline-flex items-center p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      title="Delete Project">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
          </td>
        </tr>
      `;
    }).join('');
    
    // Bind email link click handlers
    tbody.querySelectorAll('.client-email-link').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const email = e.currentTarget.getAttribute('data-email') || '';
        if (!email) {
          this.showWarning('No email on record for this client.');
          return;
        }
        try { navigator.clipboard?.writeText(email); } catch {}
        this.showNotification(`Email: ${this.escapeHtml(email)} (copied)`, 'info');
      });
    });
  }

  updateTrackerSummary() {
    // Tracker summary footer removed - function disabled
    return;
  }

  filterTrackerProjects() {
    const searchTerm = document.getElementById('tracker-search')?.value.toLowerCase() || '';
    const statusFilter = document.getElementById('tracker-status-filter')?.value || '';
    const serviceFilter = document.getElementById('tracker-service-filter')?.value || '';
    const paymentFilter = document.getElementById('tracker-payment-filter')?.value || '';

    let filteredProjects = this.projects.filter(project => {
      const matchesSearch = !searchTerm || 
        project.projectName?.toLowerCase().includes(searchTerm) ||
        project.client?.toLowerCase().includes(searchTerm) ||
        project.email?.toLowerCase().includes(searchTerm);
        
      const matchesStatus = !statusFilter || project.status === statusFilter;
      
      const matchesService = !serviceFilter || project.serviceType === serviceFilter;
      
      let matchesPayment = true;
      if (paymentFilter) {
        const outstanding = parseFloat(project.remainingToBePaid) || 0;
        const total = parseFloat(project.invoiceTotal) || 0;
        
        if (paymentFilter === 'paid') {
          matchesPayment = outstanding === 0;
        } else if (paymentFilter === 'partial') {
          matchesPayment = outstanding > 0 && outstanding < total;
        } else if (paymentFilter === 'unpaid') {
          matchesPayment = outstanding > 0 && outstanding >= total;
        }
      }
      
      return matchesSearch && matchesStatus && matchesService && matchesPayment;
    });

    // Temporarily replace projects array for table rendering
    const originalProjects = this.projects;
    this.projects = filteredProjects;
    this.populateTrackerTable();
    this.updateTrackerSummary();
    this.projects = originalProjects; // Restore original array
  }

  clearTrackerFilters() {
    document.getElementById('tracker-search').value = '';
    document.getElementById('tracker-status-filter').value = '';
    document.getElementById('tracker-service-filter').value = '';
    document.getElementById('tracker-payment-filter').value = '';
    this.loadTrackerData();
  }

  exportTrackerCSV() {
    const headers = [
      'Project Number', 'Project Name', 'Client', 'Status', 'Invoice Number', 'Invoice Date',
      'Invoice Total', '50% Cost', '50% Paid', 'Tax', 'Dev Cost', 'Net Profit',
      'Client Paid', 'Remaining to be Paid', 'Paid to Dev', 'Extra Dev Cost'
    ];

    const csvData = this.projects.map(project => [
      project.projectNumber || '',
      project.projectName || '',
      project.client || '',
      project.status || '',
      project.invoiceNumber || '',
      project.invoiceDate ? this.formatDate(project.invoiceDate) : '',
      parseFloat(project.invoiceTotal) || 0,
      parseFloat(project.fiftyPercentCost) || 0,
      project.fiftyPercentPaid || 'No',
      parseFloat(project.tax) || 0,
      parseFloat(project.devCost) || 0,
      parseFloat(project.netProfit) || 0,
      parseFloat(project.clientPaid) || 0,
      parseFloat(project.remainingToBePaid) || 0,
      parseFloat(project.paidToDev) || 0,
      parseFloat(project.extraDevCost) || 0
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `atlio-projects-tracker-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    this.showSuccess('Financial tracker exported successfully!');
  }

  populateExcelStyleTable() {
    const tbody = document.getElementById('excel-finance-table-body');
    if (!tbody) return;

    // Sort projects by project number
    const sortedProjects = [...this.projects].sort((a, b) => {
      const numA = parseInt(a.projectNumber) || 0;
      const numB = parseInt(b.projectNumber) || 0;
      return numA - numB;
    });

    tbody.innerHTML = sortedProjects.map(project => {
      const invoiceTotal = parseFloat(project.invoiceTotal) || 0;
      const fiftyPercentCost = parseFloat(project.fiftyPercentCost) || 0;
      const tax = parseFloat(project.tax) || 0;
      const devCost = parseFloat(project.devCost) || 0;
      const netProfit = parseFloat(project.netProfit) || 0;
      const clientPaid = parseFloat(project.clientPaid) || 0;
      const remainingToBePaid = parseFloat(project.remainingToBePaid) || 0;
      const paidToDev = parseFloat(project.paidToDev) || 0;
      const profitPercentage = invoiceTotal > 0 ? ((netProfit / invoiceTotal) * 100) : 0;

      return `
        <tr class="excel-row cursor-pointer hover:bg-gray-100 hover:shadow-md transition-all duration-200" onclick="this.classList.toggle('selected-row')">
          <td class="px-3 py-3 whitespace-nowrap text-sm font-bold text-black bg-transparent border-r border-gray-300">
            ${project.projectNumber || '-'}
          </td>
          <td class="px-4 py-3 whitespace-nowrap bg-transparent border-r border-gray-300">
            <div class="text-sm font-bold text-black">${this.escapeHtml(project.projectName || 'Untitled')}</div>
            <div class="text-xs font-semibold text-gray-800">${this.escapeHtml(project.client || 'N/A')}</div>
          </td>
          <td class="px-4 py-3 whitespace-nowrap text-sm text-black font-bold bg-transparent border-r border-gray-300">
            £${invoiceTotal.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-3 whitespace-nowrap text-sm text-black font-semibold bg-transparent border-r border-gray-300">
            £${fiftyPercentCost.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-3 whitespace-nowrap text-sm text-black font-semibold bg-transparent border-r border-gray-300">
            £${tax.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-3 whitespace-nowrap text-sm text-black font-semibold bg-transparent border-r border-gray-300">
            £${devCost.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-3 whitespace-nowrap text-sm text-black font-bold bg-transparent border-r border-gray-300">
            £${netProfit.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-3 whitespace-nowrap text-sm text-black font-semibold bg-transparent border-r border-gray-300">
            £${clientPaid.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-3 whitespace-nowrap text-sm text-black font-semibold bg-transparent border-r border-gray-300">
            £${remainingToBePaid.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-3 whitespace-nowrap text-sm text-black font-semibold bg-transparent border-r border-gray-300">
            £${paidToDev.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </td>
          <td class="px-4 py-3 whitespace-nowrap text-sm text-black font-bold bg-transparent">
            ${profitPercentage.toFixed(1)}%
          </td>
        </tr>
      `;
    }).join('');

    // Update Excel style summary
    this.updateExcelStyleSummary();
  }

  updateExcelStyleSummary() {
    const totalRevenue = this.projects.reduce((sum, p) => sum + (parseFloat(p.invoiceTotal) || 0), 0);
    const total50Cost = this.projects.reduce((sum, p) => sum + (parseFloat(p.fiftyPercentCost) || 0), 0);
    const totalTax = this.projects.reduce((sum, p) => sum + (parseFloat(p.tax) || 0), 0);
    const totalDev = this.projects.reduce((sum, p) => sum + (parseFloat(p.devCost) || 0), 0);
    const totalProfit = this.projects.reduce((sum, p) => sum + (parseFloat(p.netProfit) || 0), 0);
    const totalPaid = this.projects.reduce((sum, p) => sum + (parseFloat(p.clientPaid) || 0), 0);
    const totalOutstanding = this.projects.reduce((sum, p) => sum + (parseFloat(p.remainingToBePaid) || 0), 0);
    const totalPaidToDev = this.projects.reduce((sum, p) => sum + (parseFloat(p.paidToDev) || 0), 0);
    const totalProfitPercentage = totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100) : 0;

    document.getElementById('excel-total-revenue').textContent = `£${totalRevenue.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('excel-total-50cost').textContent = `£${total50Cost.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('excel-total-tax').textContent = `£${totalTax.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('excel-total-dev').textContent = `£${totalDev.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('excel-total-profit').textContent = `£${totalProfit.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('excel-total-paid').textContent = `£${totalPaid.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('excel-total-outstanding').textContent = `£${totalOutstanding.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('excel-total-devcost').textContent = `£${totalPaidToDev.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    document.getElementById('excel-total-profit-percent').textContent = `${totalProfitPercentage.toFixed(1)}%`;
  }


  async loadProjects() {
    try {
      this.showLoading(true);
      
      if (this.useFirebase && this.firebaseManager) {
        // Load projects from Firebase
        await this.firebaseManager.ensureInitialized();
        this.projects = await this.firebaseManager.getProjects();
        this.filteredProjects = [...this.projects];
        
        // Debug: Check if projects have numbers
        console.log('🔍 Loaded projects from Firebase:');
        this.projects.forEach(p => {
          console.log(`  Project: ${p.projectName}, Number: ${p.projectNumber}, ID: ${p.id}`);
        });
        
        // Set up real-time listener
        this.setupRealtimeListener();
        
        this.updateStatistics();
        this.renderProjectsTable();
      } else {
        // Use dummy data for development
        console.log('📝 Loading dummy data for development...');
        
        // Restore financial data to projects that lost it
        this.projects = this.restoreFinancialData();
        this.filteredProjects = [...this.projects];
        
        console.log(`✅ Loaded ${this.projects.length} dummy projects`);
        
        this.updateStatistics();
        this.renderProjectsTable();
      }
      
    } catch (error) {
      console.error('Error loading projects:', error);
      this.showError('Failed to load projects: ' + error.message);
    } finally {
      this.showLoading(false);
      this.updateLastUpdatedTime();
    }
  }

  setupRealtimeListener() {
    if (this.useFirebase && this.firebaseManager && this.firebaseManager.initialized) {
      this.firebaseManager.onProjectsChange((projects) => {
        this.projects = projects;
        this.filteredProjects = [...this.projects];
        this.updateStatistics();
        this.renderProjectsTable();
        this.updateLastUpdatedTime();
        this.saveProjectsToLocalStorage();
        console.log('🔄 Projects updated in real-time');
      });
    }
  }

  updateStatistics() {
    const total = this.projects.length;
    const active = this.projects.filter(p => p.active && p.status !== 'Completed').length;
    const completed = this.projects.filter(p => p.status === 'Completed').length;
    const overdue = this.projects.filter(p => this.isOverdue(p)).length;
    
    document.getElementById('total-projects-stat').textContent = total;
    document.getElementById('active-projects-stat').textContent = active;
    document.getElementById('completed-projects-stat').textContent = completed;
    document.getElementById('overdue-projects-stat').textContent = overdue;
  }

  isOverdue(project) {
    if (!project.nextMilestone) return false;
    const milestone = new Date(project.nextMilestone);
    const today = new Date();
    return milestone < today && project.status !== 'Completed';
  }

  filterProjects() {
    const searchTerm = document.getElementById('search-projects')?.value.toLowerCase() || '';
    const statusFilter = document.getElementById('status-filter')?.value || '';
    const serviceFilter = document.getElementById('service-filter')?.value || '';
    
    this.filteredProjects = this.projects.filter(project => {
      const matchesSearch = !searchTerm || 
        project.projectName?.toLowerCase().includes(searchTerm) ||
        project.client?.toLowerCase().includes(searchTerm) ||
        project.email?.toLowerCase().includes(searchTerm) ||
        project.projectNumber?.toString().includes(searchTerm);
        
      const matchesStatus = !statusFilter || project.status === statusFilter;
      const matchesService = !serviceFilter || project.serviceType === serviceFilter;
      
      return matchesSearch && matchesStatus && matchesService;
    });
    
    // Apply current sorting
    this.applySorting();
    this.renderProjectsTable();
  }

  applySorting() {
    if (!this.sortField) return;

    this.filteredProjects.sort((a, b) => {
      let aValue = a[this.sortField];
      let bValue = b[this.sortField];

      // Handle different data types
      if (this.sortField === 'projectNumber' || this.sortField === 'completion') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      } else {
        // String comparison
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }

      if (this.sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }

  clearFilters() {
    document.getElementById('search-projects').value = '';
    document.getElementById('status-filter').value = '';
    document.getElementById('service-filter').value = '';
    this.filteredProjects = [...this.projects];
    this.renderProjectsTable();
  }

  renderProjectsTable() {
    const tbody = document.getElementById('projects-table-body');
    const tableContent = document.getElementById('projects-table-content');
    const noProjects = document.getElementById('no-projects-table');
    
    if (!tbody) return;
    
    if (this.filteredProjects.length === 0) {
      tableContent?.classList.add('hidden');
      noProjects?.classList.remove('hidden');
      return;
    }
    
    tableContent?.classList.remove('hidden');
    noProjects?.classList.add('hidden');
    
    // Auto-assign project numbers if not set and apply sorting
    this.filteredProjects.forEach((project, index) => {
      if (!project.projectNumber) {
        project.projectNumber = index + 1;
        console.log(`🔢 Auto-assigned #${project.projectNumber} to ${project.projectName}`);
      }
    });


    tbody.innerHTML = this.filteredProjects.map(project => {
      // Format dates
      const firstDraftDate = project.firstDraft ? this.formatDate(project.firstDraft) : null;
      const secondDraftDate = project.secondDraft ? this.formatDate(project.secondDraft) : null;
      const finalSubmissionDate = project.finalSubmission ? this.formatDate(project.finalSubmission) : null;
      const nextMilestoneDate = project.nextMilestone ? this.formatDate(project.nextMilestone) : null;
      
      // Truncate long text fields
      const nextSteps = (project.nextSteps || '').substring(0, 50) + ((project.nextSteps || '').length > 50 ? '...' : '');
      const completed = (project.completed || '').substring(0, 60) + ((project.completed || '').length > 60 ? '...' : '');
      const inProgress = (project.inProgress || '').substring(0, 60) + ((project.inProgress || '').length > 60 ? '...' : '');
      
      return `
      <tr class="hover:bg-gray-50">
        <td class="px-3 py-4 whitespace-nowrap">
          <div class="text-sm font-bold text-white">${project.projectNumber || 'N/A'}</div>
        </td>
        <td class="px-4 py-4 whitespace-nowrap">
          <div class="text-sm font-medium text-white" data-field="projectName" data-id="${project.id}" data-editable="false">${this.escapeHtml(project.projectName || 'Untitled')}</div>
          <div class="mt-1 text-xs italic text-gray-500">
            <button type="button" class="client-email-link underline decoration-dotted hover:text-gray-700" data-email="${this.escapeHtml(project.email || '')}" title="View email">
              ${this.escapeHtml(project.client || 'N/A')}
            </button>
          </div>
        </td>
        <!-- Next Steps column removed per new order -->
        <td class="px-4 py-4 whitespace-nowrap">
          <div class="text-sm text-gray-900" data-field="serviceType" data-id="${project.id}" data-editable="false">${this.escapeHtml(project.serviceType || 'N/A')}</div>
        </td>
        <td class="px-0 py-0 ${this.getStatusBg(project.status)}">
          <div class="w-full h-full min-h-[56px] flex items-center justify-center" data-field="status" data-id="${project.id}" data-editable="true">
            <span class="text-sm font-semibold text-white whitespace-nowrap">${this.escapeHtml(project.status || 'Unknown')}</span>
          </div>
        </td>
        <td class="px-4 py-4">
          <div class="text-sm text-white max-w-xs" data-field="completed" data-id="${project.id}" data-editable="true">${this.escapeHtml(completed || '-')}</div>
        </td>
        <td class="px-4 py-4">
          <div class="text-sm text-white max-w-xs" data-field="inProgress" data-id="${project.id}" data-editable="true">${this.escapeHtml(inProgress || '-')}</div>
        </td>
        <td class="px-4 py-4" style="min-width: 380px;">
          ${this.renderTimelineCell(project)}
        </td>
        <!-- Active column removed -->
        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium actions-cell">
          <div class="flex items-center justify-end space-x-2">
            <button data-action="edit" data-project-id="${project.id || ''}" 
                    class="project-action-btn inline-flex items-center p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                    title="Edit Project">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </button>
            <button data-action="delete" data-project-id="${project.id || ''}" 
                    class="project-action-btn inline-flex items-center p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    title="Delete Project">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </button>
          </div>
        </td>
      </tr>
    `;
    }).join('');
    
    // Also update Excel style table and finance data when projects table is rendered
    this.populateExcelStyleTable();
    this.updateFinanceStats();
    
    // Save projects to localStorage for customer access
    this.saveProjectsToLocalStorage();
    
    // Enable inline editing when edit mode is on
    this.applyInlineEditing();
  }

  saveProjectsToLocalStorage() {
    try {
      localStorage.setItem('atlioProjects', JSON.stringify(this.projects));
      console.log('Projects saved to localStorage for customer access');
    } catch (error) {
      console.error('Error saving projects to localStorage:', error);
    }
  }

  getStatusClass(status) {
    const statusLower = (status || '').toLowerCase();
    // Completed status - GREEN
    if (statusLower.includes('completed')) return 'bg-green-100 text-green-800';
    // In Progress statuses - BLUE
    if (statusLower.includes('progress')) return 'bg-blue-100 text-blue-800';
    // Draft Done statuses - GREEN (same as completed)
    if (statusLower.includes('draft done')) return 'bg-green-100 text-green-800';
    // Invoice Sent - GREEN (same as completed)
    if (statusLower.includes('invoice sent')) return 'bg-green-100 text-green-800';
    // Awaiting Feedback - YELLOW (looks orange: #D97706)
    if (statusLower.includes('awaiting') || statusLower.includes('feedback')) return 'bg-yellow-100 text-yellow-800';
    // Not Won - GRAY (same as Not Started)
    if (statusLower.includes('not won')) return 'bg-gray-100 text-gray-800';
    // On Hold/Cancelled - RED
    if (statusLower.includes('hold') || statusLower.includes('cancelled')) return 'bg-red-100 text-red-800';
    // Not Started (default) - GRAY
    return 'bg-gray-100 text-gray-800';
  }

  getStatusBg(status) {
    const s = (status || '').toLowerCase();
    // Completed status - GREEN
    if (s.includes('completed')) return 'bg-green-600';
    // In Progress statuses - BLUE
    if (s.includes('progress')) return 'bg-blue-600';
    // Draft Done statuses - GREEN (same as completed)
    if (s.includes('draft done')) return 'bg-green-600';
    // Invoice Sent - GREEN (same as completed)
    if (s.includes('invoice sent')) return 'bg-green-600';
    // Awaiting Feedback - YELLOW (looks orange: #D97706)
    if (s.includes('awaiting') || s.includes('feedback')) return 'bg-yellow-600';
    // Not Won - GRAY (same as Not Started)
    if (s.includes('not won')) return 'bg-gray-600';
    // On Hold/Cancelled - RED
    if (s.includes('hold') || s.includes('cancelled')) return 'bg-red-600';
    // Not Started (default) - GRAY
    return 'bg-gray-600';
  }

  openProjectModal(project = null) {
    this.currentProject = project;
    const modal = document.getElementById('project-modal');
    const title = document.getElementById('modal-title');
    const form = document.getElementById('project-form');
    
    if (project) {
      title.textContent = 'Edit Project';
      this.populateForm(project);
      // Update progress bar handle position
      setTimeout(() => {
        const completion = project.completion || 0;
        this.updateProgressBarHandle(completion);
      }, 100);
    } else {
      title.textContent = 'New Project';
      form.reset();
      const completionElement = document.getElementById('completion-value');
      if (completionElement) {
        completionElement.textContent = '0%';
      }
      
      // Auto-assign next project number
      const maxNumber = Math.max(...this.projects.map(p => parseInt(p.projectNumber) || 0), 0);
      const nextNumber = maxNumber + 1;
      document.getElementById('projectNumber').value = nextNumber;
      
      // Reset progress bar handle position
      setTimeout(() => {
        this.updateProgressBarHandle(0);
      }, 100);
    }
    
    if (modal) {
      modal.classList.remove('hidden');
    }
    document.body.style.overflow = 'hidden';
  }
  
  updateProgressBarHandle(percentage) {
    const modalHandle = document.getElementById('modal-progress-drag-handle');
    const progressFill = document.getElementById('progress-fill');
    const valueDisplay = document.getElementById('completion-value');
    const hiddenInput = document.getElementById('completion');
    
    if (modalHandle) {
      modalHandle.style.left = `${percentage}%`;
    }
    if (progressFill) {
      progressFill.style.width = `${percentage}%`;
    }
    if (valueDisplay) {
      valueDisplay.textContent = `${percentage}%`;
    }
    if (hiddenInput) {
      hiddenInput.value = percentage;
    }
  }

  closeModal() {
    document.getElementById('project-modal')?.classList.add('hidden');
    document.body.style.overflow = 'auto';
    this.currentProject = null;
  }

  populateForm(project) {
    const fields = [
      'id', 'projectNumber', 'projectName', 'client', 'email', 'serviceType', 'status', 
      'completion', 'nextSteps', 'completed', 'inProgress', 
      'firstDraft', 'secondDraft', 'finalSubmission', 'nextMilestone', 'active',
      'version', 'projectType', 'priority', 'shopifyThemeId', 'shopifyStoreUrl',
      'shopifyStoreDomain', 'shopifyCustomerId', 'notes',
      // Financial fields
      'invoiceNumber', 'invoiceDate', 'invoiceTotal', 'fiftyPercentCost', 'fiftyPercentPaid',
      'tax', 'devCost', 'netProfit', 'clientPaid', 'remainingToBePaid', 'paidToDev', 'extraDevCost'
    ];
    
    fields.forEach(field => {
      const element = document.getElementById(field);
      if (element) {
        if (element.type === 'checkbox') {
          element.checked = project[field] || false;
        } else if (element.type === 'date' && project[field]) {
          element.value = this.formatDateForInput(project[field]);
        } else {
          element.value = project[field] || '';
        }
      }
    });
    
    // Handle tags array
    if (project.tags && Array.isArray(project.tags)) {
      document.getElementById('tags').value = project.tags.join(', ');
    }
    
    // Update progress display and bar
    const completion = project.completion || 0;
    const completionValue = document.getElementById('completion-value');
    const progressFill = document.getElementById('progress-fill');
    const progressHandle = document.getElementById('progress-drag-handle');
    
    if (completionValue) {
      completionValue.textContent = completion + '%';
    }
    if (progressFill) {
      progressFill.style.width = completion + '%';
    }
    if (progressHandle) {
      progressHandle.style.left = completion + '%';
    }
  }

  async handleFormSubmit(e) {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const projectData = {};
    
    // Convert form data to object
    for (let [key, value] of formData.entries()) {
      if (key === 'active') {
        projectData[key] = true; // Checkbox is checked if present
      } else if (key === 'completion' || key === 'projectNumber' || key === 'invoiceNumber') {
        projectData[key] = parseInt(value) || 0;
      } else if (key === 'invoiceTotal' || key === 'fiftyPercentCost' || key === 'devCost' || key === 'netProfit' || key === 'clientPaid' || key === 'remainingToBePaid' || key === 'paidToDev' || key === 'tax' || key === 'extraDevCost') {
        projectData[key] = parseFloat(value) || 0;
      } else if (key === 'tags') {
        // Convert comma-separated tags to array
        projectData[key] = value ? value.split(',').map(tag => tag.trim()).filter(tag => tag) : [];
      } else if (key === 'hoursEstimation' || key === 'hoursTaken') {
        projectData[key] = parseFloat(value) || 0;
      } else {
        projectData[key] = value || null;
      }
    }

    // Auto-assign project number if not provided
    if (!projectData.projectNumber) {
      const maxNumber = Math.max(...this.projects.map(p => parseInt(p.projectNumber) || 0), 0);
      projectData.projectNumber = maxNumber + 1;
    }
    
    // Handle checkbox when unchecked
    if (!formData.has('active')) {
      projectData.active = false;
    }
    
    // Set default values for new Shopify fields if creating new project
    if (!this.currentProject) {
      projectData.shopifyThemeId = projectData.shopifyThemeId || '187271971158';
      projectData.shopifyStoreUrl = projectData.shopifyStoreUrl || 'https://atlio.myshopify.com/';
      projectData.shopifyStoreDomain = projectData.shopifyStoreDomain || 'atlio.myshopify.com';
      projectData.version = projectData.version || '1.0.0';
      projectData.projectType = projectData.projectType || 'Standard';
      projectData.priority = projectData.priority || 'Medium';
    }
    
    try {
      const isEdit = this.currentProject && this.currentProject.id;
      if (this.useFirebase && this.firebaseManager) {
        // Save to Firebase
        await this.firebaseManager.ensureInitialized();
        
        if (isEdit) {
          await this.firebaseManager.updateProject(this.currentProject.id, projectData);
          this.showSuccess('Project updated successfully!');
        } else {
          await this.firebaseManager.createProject(projectData);
          this.showSuccess('Project created successfully!');
        }
        
        this.closeModal();
        // Real-time listener will update the UI automatically
      } else {
        // Use dummy data for development
        if (isEdit) {
          this.updateDummyProject(this.currentProject.id, projectData);
          this.showSuccess('Project updated successfully! (Dummy Data)');
        } else {
          this.createDummyProject(projectData);
          this.showSuccess('Project created successfully! (Dummy Data)');
        }
        
        this.closeModal();
        // Reload projects to refresh UI
        await this.loadProjects();
      }
      
    } catch (error) {
      console.error('Error saving project:', error);
      this.showError('Failed to save project: ' + error.message);
    }
  }

  async editProject(projectId) {
    const project = this.projects.find(p => p.id === projectId);
    
    if (project) {
      this.openProjectModal(project);
    } else {
      this.showError('Project not found. Please refresh the page.');
    }
  }

  async deleteProject(projectId) {
    // Find the project to get its name
    const project = this.projects.find(p => p.id === projectId);
    
    if (!project) {
      this.showError('Project not found. Please refresh the page.');
      return;
    }
    
    const projectName = project.projectName || 'Unknown Project';
    
    // Show custom confirmation modal
    const confirmed = await this.showDeleteConfirmation(projectName);
    if (!confirmed) {
      return;
    }
    
    try {
      if (this.useFirebase && this.firebaseManager) {
        // Delete from Firebase
        await this.firebaseManager.ensureInitialized();
        await this.firebaseManager.deleteProject(projectId);
        this.showSuccess('Project deleted successfully!');
        // Real-time listener will update the UI automatically
      } else {
        // Use dummy data for development
        this.deleteDummyProject(projectId);
        this.showSuccess('Project deleted successfully! (Dummy Data)');
        await this.loadProjects();
      }
      
    } catch (error) {
      console.error('Error deleting project:', error);
      this.showError('Failed to delete project: ' + error.message);
    }
  }

  async showDeleteConfirmation(projectName) {
    return new Promise((resolve) => {
      // Create modal HTML
      const modalHTML = `
        <div id="delete-confirmation-modal" class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div class="bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700 max-w-md w-full animate-in slide-in-from-bottom-4 duration-300">
            <div class="text-center mb-6">
              <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-bold text-white mb-2">Delete Project</h3>
              <p class="text-gray-400 mb-4">This action cannot be undone. This will permanently delete the project.</p>
              
              <div class="bg-gray-700/50 rounded-lg p-4 mb-6">
                <p class="text-sm text-gray-300 mb-2">Project to delete:</p>
                <p class="text-lg font-semibold text-white">${this.escapeHtml(projectName)}</p>
              </div>
              
              <div class="text-left">
                <label for="delete-confirmation-input" class="block text-sm font-medium text-gray-300 mb-2">
                  Type the project name to confirm deletion:
                </label>
                <input type="text" 
                       id="delete-confirmation-input" 
                       class="w-full px-4 py-3 bg-gray-700 border-2 border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-red-400 focus:outline-none focus:ring-0 transition-all duration-200"
                       placeholder="Type project name here..."
                       autocomplete="off">
                <div class="mt-2 text-xs text-gray-400">
                  💡 Tip: You can copy and paste the project name from above
                </div>
              </div>
            </div>
            
            <div class="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
              <button id="cancel-delete" 
                      class="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-500 text-white font-semibold rounded-lg transition-colors duration-200">
                Cancel
              </button>
              <button id="confirm-delete" 
                      class="flex-1 px-4 py-3 bg-red-900 text-white font-semibold rounded-lg transition-all duration-200 opacity-50 cursor-not-allowed"
                      disabled>
                Delete Project
              </button>
            </div>
          </div>
        </div>
      `;
      
      // Add modal to DOM
      document.body.insertAdjacentHTML('beforeend', modalHTML);
      
      const modal = document.getElementById('delete-confirmation-modal');
      const input = document.getElementById('delete-confirmation-input');
      const confirmBtn = document.getElementById('confirm-delete');
      const cancelBtn = document.getElementById('cancel-delete');
      
      // Enable delete button only when text matches
      input.addEventListener('input', () => {
        const inputValue = input.value.trim();
        const matches = inputValue === projectName;
        
        if (matches) {
          confirmBtn.disabled = false;
          confirmBtn.classList.remove('opacity-50', 'cursor-not-allowed', 'bg-red-900');
          confirmBtn.classList.add('opacity-100', 'cursor-pointer', 'bg-red-600', 'hover:bg-red-700');
        } else {
          confirmBtn.disabled = true;
          confirmBtn.classList.add('opacity-50', 'cursor-not-allowed', 'bg-red-900');
          confirmBtn.classList.remove('opacity-100', 'cursor-pointer', 'bg-red-600', 'hover:bg-red-700');
        }
      });
      
      // Handle confirmation
      confirmBtn.addEventListener('click', () => {
        if (input.value.trim() === projectName) {
          modal.remove();
          resolve(true);
        }
      });
      
      // Handle cancellation
      cancelBtn.addEventListener('click', () => {
        modal.remove();
        resolve(false);
      });
      
      // Handle escape key
      document.addEventListener('keydown', function escapeHandler(e) {
        if (e.key === 'Escape') {
          modal.remove();
          document.removeEventListener('keydown', escapeHandler);
          resolve(false);
        }
      });
      
      // Handle backdrop click
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.remove();
          resolve(false);
        }
      });
      
      // Focus the input
      setTimeout(() => input.focus(), 100);
    });
  }

  async syncToFirebase() {
    try {
      if (this.useFirebase && this.firebaseManager) {
        // Update statistics in Firebase
        await this.firebaseManager.ensureInitialized();
        const stats = await this.firebaseManager.updateStatistics();
        this.showSuccess(`Firebase data synchronized! Total projects: ${stats.totalProjects}`);
      } else {
        this.showError('Firebase not configured. Please check your settings.');
      }
    } catch (error) {
      console.error('Error syncing to Firebase:', error);
      this.showError('Failed to sync to Firebase: ' + error.message);
    }
  }

  exportData() {
    const dataStr = JSON.stringify(this.projects, null, 2);
    const dataBlob = new Blob([dataStr], {type: 'application/json'});
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `atlio-projects-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
    this.showSuccess('Project data exported successfully!');
  }

  showLoading(show) {
    const loading = document.getElementById('table-loading');
    const content = document.getElementById('projects-table-content');
    
    if (show) {
      loading?.classList.remove('hidden');
      content?.classList.add('hidden');
    } else {
      loading?.classList.add('hidden');
      content?.classList.remove('hidden');
    }
  }

  showSuccess(message) {
    this.showNotification(message, 'success');
  }

  showError(message) {
    this.showNotification(message, 'error');
  }

  showWarning(message) {
    this.showNotification(message, 'warning');
  }

  showNotification(message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `fixed top-4 left-1/2 transform -translate-x-1/2 px-6 py-4 rounded-lg shadow-lg max-w-sm transition-all duration-300 text-white`;
    notification.style.zIndex = '9999';
    
    // Set background color based on type
    if (type === 'success') {
      notification.style.backgroundColor = '#10B981'; // Green-500
      notification.innerHTML = `
        <div class="flex items-center">
          <svg class="w-5 h-5 mr-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          <div class="flex-1">${this.escapeHtml(message)}</div>
          <button onclick="this.parentElement.parentElement.remove()" class="ml-4 text-white hover:text-gray-200">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      `;
    } else if (type === 'error') {
      notification.style.backgroundColor = '#EF4444'; // Red-500
      notification.innerHTML = `
        <div class="flex items-center">
          <svg class="w-5 h-5 mr-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <div class="flex-1">${this.escapeHtml(message)}</div>
          <button onclick="this.parentElement.parentElement.remove()" class="ml-4 text-white hover:text-gray-200">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      `;
    } else if (type === 'warning') {
      notification.style.backgroundColor = '#F59E0B'; // Yellow-500
      notification.innerHTML = `
        <div class="flex items-center">
          <svg class="w-5 h-5 mr-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
          </svg>
          <div class="flex-1">${this.escapeHtml(message)}</div>
          <button onclick="this.parentElement.parentElement.remove()" class="ml-4 text-white hover:text-gray-200">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      `;
    } else {
      notification.style.backgroundColor = '#3B82F6'; // Blue-500
      notification.innerHTML = `
        <div class="flex items-center">
          <svg class="w-5 h-5 mr-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <div class="flex-1">${this.escapeHtml(message)}</div>
          <button onclick="this.parentElement.parentElement.remove()" class="ml-4 text-white hover:text-gray-200">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      `;
    }
    
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }

  showEmailPopup(email) {
    // Create simple modal
    const existing = document.getElementById('email-popup-modal');
    if (existing) existing.remove();
    const wrap = document.createElement('div');
    wrap.id = 'email-popup-modal';
    wrap.className = 'fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4';
    wrap.innerHTML = `
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div class="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h4 class="text-sm font-semibold text-gray-900">Client Email</h4>
          <button class="p-1 rounded hover:bg-gray-100 text-gray-500" id="email-popup-close" aria-label="Close">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="px-5 py-4">
          <div class="text-sm text-gray-700 break-all">${this.escapeHtml(email)}</div>
        </div>
        <div class="px-5 py-4 border-t border-gray-200 flex justify-end space-x-2">
          <button id="email-popup-copy" class="px-3 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700">Copy</button>
          <button id="email-popup-ok" class="px-3 py-2 text-sm rounded-md bg-gray-200 text-gray-900 hover:bg-gray-300">OK</button>
        </div>
      </div>
    `;
    document.body.appendChild(wrap);
    const close = () => wrap.remove();
    wrap.addEventListener('click', (e) => { if (e.target === wrap) close(); });
    wrap.querySelector('#email-popup-close').addEventListener('click', close);
    wrap.querySelector('#email-popup-ok').addEventListener('click', close);
    wrap.querySelector('#email-popup-copy').addEventListener('click', async () => {
      try { await navigator.clipboard.writeText(email); this.showSuccess('Email copied'); }
      catch { this.showWarning('Copy failed'); }
    });
  }
  formatDate(dateString) {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return '';
    }
  }

  formatDateForInput(dateString) {
    if (!dateString) return '';
    try {
      return new Date(dateString).toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  getProgressColor(completion) {
    const progress = parseInt(completion) || 0;
    if (progress === 0) return 'bg-gray-400';
    if (progress <= 25) return 'bg-yellow-400';
    if (progress <= 50) return 'bg-yellow-500';
    if (progress <= 75) return 'bg-green-400';
    if (progress < 100) return 'bg-green-500';
    return 'bg-green-600';
  }

  getProgressTextColor(completion) {
    const progress = parseInt(completion) || 0;
    if (progress === 0) return 'text-gray-600';
    if (progress <= 25) return 'text-yellow-600';
    if (progress <= 50) return 'text-yellow-700';
    if (progress <= 75) return 'text-green-600';
    if (progress < 100) return 'text-green-600';
    return 'text-green-700';
  }

  // ============= EXPENSE MANAGEMENT METHODS =============

  loadExpenses() {
    try {
      const stored = localStorage.getItem(this.expensesStorageKey);
      this.expenses = stored ? JSON.parse(stored) : this.generateDummyExpenses();
      
      // Clean up any duplicate IDs
      this.cleanupDuplicateExpenseIds();
      
      this.renderExpensesTable();
      this.updateExpenseStats();
    } catch (error) {
      console.error('Error loading expenses:', error);
      this.expenses = this.generateDummyExpenses();
    }
  }
  
  cleanupDuplicateExpenseIds() {
    console.log('=== CHECKING FOR DUPLICATE EXPENSE IDs ===');
    const seenIds = new Set();
    const uniqueExpenses = [];
    let duplicatesFound = 0;
    
    this.expenses.forEach((expense, index) => {
      console.log(`Checking expense ${index + 1}: ID="${expense.id}"`);
      
      if (expense.id && seenIds.has(expense.id)) {
        // Duplicate ID found, generate a new unique ID
        const oldId = expense.id;
        expense.id = `exp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        console.warn(`⚠️ DUPLICATE ID FOUND! Changed "${oldId}" to "${expense.id}" for expense: ${expense.description}`);
        duplicatesFound++;
      }
      seenIds.add(expense.id);
      uniqueExpenses.push(expense);
    });
    
    if (duplicatesFound > 0) {
      console.warn(`✅ Cleaned up ${duplicatesFound} duplicate expense ID(s)`);
      this.expenses = uniqueExpenses;
      this.saveExpenses();
    } else {
      console.log('✅ No duplicate expense IDs found');
    }
  }

  generateDummyExpenses() {
    const now = new Date();
    return [
      {
        id: 'exp-1',
        date: new Date(now.getFullYear(), now.getMonth(), 5).toISOString().split('T')[0],
        category: 'Software & Subscriptions',
        description: 'Shopify monthly subscription',
        vendor: 'Shopify Inc',
        amount: 299,
        paymentMethod: 'Credit Card',
        receiptNumber: 'SHP-2024-001'
      },
      {
        id: 'exp-2',
        date: new Date(now.getFullYear(), now.getMonth(), 12).toISOString().split('T')[0],
        category: 'Marketing & Advertising',
        description: 'Google Ads campaign',
        vendor: 'Google Ads',
        amount: 850,
        paymentMethod: 'Credit Card',
        receiptNumber: 'GA-2024-102'
      },
      {
        id: 'exp-3',
        date: new Date(now.getFullYear(), now.getMonth() - 1, 20).toISOString().split('T')[0],
        category: 'Professional Services',
        description: 'Accounting services',
        vendor: 'ABC Accounting Ltd',
        amount: 450,
        paymentMethod: 'Bank Transfer',
        receiptNumber: 'INV-4523'
      }
    ];
  }

  saveExpenses() {
    try {
      localStorage.setItem(this.expensesStorageKey, JSON.stringify(this.expenses));
    } catch (error) {
      console.error('Error saving expenses:', error);
    }
  }

  renderExpensesTable() {
    const tbody = document.getElementById('expenses-table-body');
    if (!tbody) return;

    if (this.expenses.length === 0) {
      tbody.innerHTML = `
        <tr>
          <td colspan="8" class="px-6 py-8 text-center text-gray-500">
            <svg class="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
            </svg>
            <p class="text-lg font-medium">No expenses recorded yet</p>
            <p class="text-sm mt-2">Click "Add Expense" to record your first expense</p>
          </td>
        </tr>
      `;
      return;
    }

    tbody.innerHTML = this.expenses.map(expense => `
      <tr>
        <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-900">${this.formatDate(expense.date)}</td>
        <td class="px-4 py-4 whitespace-nowrap">
          <span class="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">${this.escapeHtml(expense.category)}</span>
        </td>
        <td class="px-4 py-4 text-sm text-gray-900">${this.escapeHtml(expense.description)}</td>
        <td class="px-4 py-4 text-sm text-gray-700">${this.escapeHtml(expense.vendor)}</td>
        <td class="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">£${parseFloat(expense.amount).toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
        <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-700">${this.escapeHtml(expense.paymentMethod)}</td>
        <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-500">${this.escapeHtml(expense.receiptNumber || '-')}</td>
        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div class="flex items-center justify-end space-x-2">
            <button data-action="edit-expense" data-expense-id="${expense.id}" 
                    class="expense-action-btn inline-flex items-center p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                    title="Edit Expense">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </button>
            <button data-action="delete-expense" data-expense-id="${expense.id}" 
                    class="expense-action-btn inline-flex items-center p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    title="Delete Expense">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </button>
          </div>
        </td>
      </tr>
    `).join('');
  }

  updateExpenseStats() {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const totalExpenses = this.expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
    
    const monthExpenses = this.expenses
      .filter(exp => {
        const expDate = new Date(exp.date);
        return expDate.getMonth() === currentMonth && expDate.getFullYear() === currentYear;
      })
      .reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);

    const yearExpenses = this.expenses
      .filter(exp => new Date(exp.date).getFullYear() === currentYear)
      .reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);

    const avgExpenses = totalExpenses / Math.max(1, new Set(this.expenses.map(e => new Date(e.date).getMonth() + '-' + new Date(e.date).getFullYear())).size);

    document.getElementById('total-expenses').textContent = '£' + totalExpenses.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    document.getElementById('month-expenses').textContent = '£' + monthExpenses.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    document.getElementById('year-expenses').textContent = '£' + yearExpenses.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    document.getElementById('avg-expenses').textContent = '£' + avgExpenses.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  }

  openExpenseModal(expense = null) {
    this.currentExpense = expense;
    const modal = document.getElementById('expense-modal');
    const title = document.getElementById('expense-modal-title');
    const form = document.getElementById('expense-form');
    
    if (!modal || !title || !form) return;
    
    if (expense) {
      title.textContent = 'Edit Expense';
      this.populateExpenseForm(expense);
    } else {
      title.textContent = 'Add Expense';
      form.reset();
      // Explicitly clear the hidden ID field to prevent duplicate IDs
      const idInput = document.getElementById('expense-id');
      if (idInput) {
        idInput.value = '';
      }
      const dateInput = document.getElementById('expense-date');
      if (dateInput) {
        dateInput.value = new Date().toISOString().split('T')[0];
      }
    }
    
    modal.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
  }

  closeExpenseModal() {
    const modal = document.getElementById('expense-modal');
    if (modal) {
      modal.classList.add('hidden');
    }
    document.body.style.overflow = 'auto';
    this.currentExpense = null;
    
    // Reset the form to prevent ID contamination
    const form = document.getElementById('expense-form');
    if (form) {
      form.reset();
    }
  }

  populateExpenseForm(expense) {
    const fields = ['id', 'date', 'category', 'description', 'vendor', 'amount', 'paymentMethod', 'receiptNumber', 'notes'];
    
    fields.forEach(field => {
      const element = document.getElementById(`expense-${field}`);
      if (element) {
        element.value = expense[field] || '';
      }
    });
  }

  async handleExpenseFormSubmit(e) {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const formId = formData.get('id');
    
    // Generate a unique ID for new expenses
    let expenseId;
    if (this.currentExpense) {
      // Editing existing expense - keep the same ID
      expenseId = this.currentExpense.id;
    } else {
      // New expense - generate unique ID
      expenseId = `exp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
    
    const expenseData = {
      id: expenseId,
      date: formData.get('date'),
      category: formData.get('category'),
      description: formData.get('description'),
      vendor: formData.get('vendor'),
      amount: parseFloat(formData.get('amount')) || 0,
      paymentMethod: formData.get('paymentMethod'),
      receiptNumber: formData.get('receiptNumber') || '',
      notes: formData.get('notes') || '',
      createdAt: this.currentExpense ? this.currentExpense.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    if (this.currentExpense) {
      // Update existing expense
      const index = this.expenses.findIndex(e => e.id === expenseData.id);
      if (index !== -1) {
        this.expenses[index] = expenseData;
        this.showSuccess('Expense updated successfully!');
      }
    } else {
      // Add new expense - double check no duplicate exists
      const existingIndex = this.expenses.findIndex(e => e.id === expenseData.id);
      if (existingIndex !== -1) {
        console.error('Duplicate ID detected, generating new one');
        expenseData.id = `exp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }
      this.expenses.push(expenseData);
      this.showSuccess('Expense added successfully!');
    }

    this.saveExpenses();
    this.renderExpensesTable();
    this.updateExpenseStats();
    this.closeExpenseModal();
  }

  editExpense(expenseId) {
    const expense = this.expenses.find(e => e.id === expenseId);
    if (expense) {
      this.openExpenseModal(expense);
    } else {
      this.showError('Expense not found.');
    }
  }

  async deleteExpense(expenseId) {
    // Add validation and debugging
    console.log('=== DELETE EXPENSE DEBUG ===');
    console.log('Deleting expense with ID:', expenseId);
    console.log('Type of expenseId:', typeof expenseId);
    console.log('Current expenses count:', this.expenses.length);
    
    // Log each expense ID separately for clarity
    this.expenses.forEach((exp, index) => {
      console.log(`Expense ${index + 1}: ID="${exp.id}" (type: ${typeof exp.id}), Description: "${exp.description}"`);
    });
    
    // Check how many expenses match this ID
    const matchingExpenses = this.expenses.filter(e => e.id === expenseId);
    console.log('Number of expenses matching this ID:', matchingExpenses.length);
    if (matchingExpenses.length > 1) {
      console.error('DUPLICATE ID FOUND! Multiple expenses have the same ID:', expenseId);
      matchingExpenses.forEach((exp, i) => {
        console.error(`  Duplicate ${i + 1}:`, exp);
      });
    }
    
    if (!expenseId) {
      this.showError('Invalid expense ID.');
      return;
    }
    
    const expense = this.expenses.find(e => e.id === expenseId);
    if (!expense) {
      this.showError('Expense not found.');
      return;
    }

    if (confirm(`Are you sure you want to delete this expense?\n\n${expense.description} - £${expense.amount}`)) {
      const beforeCount = this.expenses.length;
      this.expenses = this.expenses.filter(e => e.id !== expenseId);
      const afterCount = this.expenses.length;
      
      console.log(`Deleted ${beforeCount - afterCount} expense(s). Before: ${beforeCount}, After: ${afterCount}`);
      
      if (beforeCount - afterCount !== 1) {
        console.error('WARNING: Expected to delete 1 expense, but deleted', beforeCount - afterCount);
      }
      
      this.saveExpenses();
      this.renderExpensesTable();
      this.updateExpenseStats();
      this.showSuccess('Expense deleted successfully!');
    }
  }

  exportExpenses() {
    if (this.expenses.length === 0) {
      this.showError('No expenses to export');
      return;
    }

    const csv = this.convertExpensesToCSV(this.expenses);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `company-expenses-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    this.showSuccess('Expenses exported successfully!');
  }

  convertExpensesToCSV(expenses) {
    const headers = ['Date', 'Category', 'Description', 'Vendor', 'Amount', 'Payment Method', 'Receipt Number', 'Notes'];
    const rows = expenses.map(e => [
      e.date,
      e.category,
      e.description,
      e.vendor,
      e.amount,
      e.paymentMethod,
      e.receiptNumber || '',
      e.notes || ''
    ]);

    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
  }

}

// Initialize admin panel when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  if (document.querySelector('.admin-project-panel')) {
    window.adminPanel = new AdminProjectPanel();
  }
});

